import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useDebounce } from "minimal-shared";
import Grid from "@mui/material/Grid2";
import {
  Button,
  Paper,
  Typography,
  Snackbar,
  Alert,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { Iconify } from "../../../Minimal/src/components/iconify/iconify";
import CustomerSearch, { formatCustomerDisplayLabel } from "../../Epos/component/CustomerSearch";
import InventorySearchModal from "../../QuickOrder/component/InventorySearchModal";
import { hasSearchableProductQuery } from "../../../../productSearch/matchProductTokens";
import CartTable from "../../Epos/component/CartTable";
import CartSummary from "../../Epos/component/CartSummary";
import RhpActionButtons from "../component/RhpActionButtons";
import PromotionSummaryCard from "../../Epos/component/PromotionSummaryCard";
import Config from "../../../common/Config";
import PromotionsDialog from "../../EposEdit/component/PromotionsDialog";
import ProductLinePromotionsDialog from "../../../common/ProductLinePromotionsDialog";
import PromotionDetailsDrawer from "../../Promotion/components/promo-details-drawer";
import SalesStockLimitDialog from "../../common/SalesStockLimitDialog";
import {
  validateMaximumOrderQuantityForCartAdd,
  validateMaximumOrderQuantityForLineQty,
  mergeOrderLimitFieldsFromCartLines,
} from "../../common/maximumOrderQuantity";
import PromotionGetProductsDialog from "../../../common/PromotionGetProductsDialog";
import {
  getStockCapForCartLineItem,
  isQuantityWithinLineStock,
} from "../../Promotion/utils/promotionLineStock";
import { getTelecallerProductDisplayName } from "../../Epos/utils/telecallerStockLimit";
import {
  syncCartQtyValidationMap,
  computeCartQuantityChange,
  applyCartQtyValidationResult,
  validateCartStockBeforePlaceOrder,
  getProductQtyTotalInCart,
} from "../../Epos/utils/cartQtyValidation";
import { ConfirmDialog } from "../../../common/confirm-dialog";
import {
  getCustomerList,
  getProductsList,
  fetchProductForPriceBrand,
  getCartIncompleteOrders,
  placeOrder,
  fetchProductStockMapForCartLines,
} from "../../../../data/epos";
import {
  getCartPromotions,
  changePromotion,
  resolvePreviousPromotionIdForChange,
  collectProductIdsSharingPromotionWithLine,
  buildCartLinePromotionsFallback,
  resolveFreshCartLineForPromotionPopup,
} from "../../../../data/promotions";
import { useEnhancedCartLogic } from "../../Epos/hooks/useEnhancedCartLogic";
import {
  PLATFORMS,
  clearCartByCustomer,
  remove_promotions,
} from "../../../../data/cart";
import {
  determineItemType,
  getOriginalPrice,
  getPriceForItem,
  prepareProductForCart,
  getPriceToSendToAPI,
  isMeatsProduct,
  allowsDecimalQty,
  parseQty,
  cartLineHasPromoOfferQtyControls,
  resolveRhpCartLineListPrice,
  isRhpRefundCartLine,
} from "../../Epos/utils/cartCalculations";
import productSplitService from "../../../../services/ProductSplitService";
import NumericKeypad from "../../../common/NumericKeypad";
import RhpLineDiscountDialog from "../component/RhpLineDiscountDialog";
import RhpLinePriceDialog from "../component/RhpLinePriceDialog";
import RhpStockDialog from "../component/RhpStockDialog";
import { resolveCheckStockBeforeAddToCart } from "../../../../utils/companySettingsCache";
import { useCompanySettings } from "../../../../contexts/CompanySettingsContext";
import { getRhpInvoicePrintUrl } from "../../Epos/utils/rhpInvoicePrint";
import {
  getCartItemFromLookup,
  getCartItemIndexFromLookup,
  getCartItemLookupMap,
} from "../../Promotion/utils/promotionOfferProductsShared";

const PRICE_BRAND_OPTIONS = [
  { value: "1", label: "Selling Band 1" },
  { value: "2", label: "Selling Band 2" },
  { value: "3", label: "Selling Band 3" },
  { value: "4", label: "Selling Band 4" },
  { value: "5", label: "Selling Band 5" },
  { value: "6", label: "Selling Band 6" },
];

const normalizePriceBrand = (value) => {
  const parsed = parseInt(String(value ?? "").trim(), 10);
  return parsed >= 1 && parsed <= 6 ? String(parsed) : "1";
};

const NewEposRhp = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [openIncompleteOrderDialog, setOpenIncompleteOrderDialog] =
    useState(false);
  const [searchIncompleteOrders, setSearchIncompleteOrders] = useState("");
  const [customerSearchInput, setCustomerSearchInput] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedPriceBrand, setSelectedPriceBrand] = useState("1");
  const [selectedCartRowIndex, setSelectedCartRowIndex] = useState(null);
  const [selectedCartRow, setSelectedCartRow] = useState(null);
  const [rhpDiscountDialogOpen, setRhpDiscountDialogOpen] = useState(false);
  const [rhpPriceDialogOpen, setRhpPriceDialogOpen] = useState(false);
  const [rhpStockDialogOpen, setRhpStockDialogOpen] = useState(false);
  const [productSearchInput, setProductSearchInput] = useState("");
  const [totalInvDiscPercent, setTotalInvDiscPercent] = useState(0);
  const [totalInvDiscType, setTotalInvDiscType] = useState("percentage");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });
  const [stockLimitDialog, setStockLimitDialog] = useState({
    open: false,
    productName: "",
    maxQty: 0,
    message: "",
    plainTitle: undefined,
  });
  const showSnackbar = useCallback((first, second = "error") => {
    if (
      first &&
      typeof first === "object" &&
      !Array.isArray(first) &&
      typeof first.message === "string"
    ) {
      setSnackbar({
        open: first.open !== false,
        severity: first.severity ?? "warning",
        message: first.message,
        useErrorDialog: Boolean(first.useErrorDialog),
      });
      return;
    }
    setSnackbar({
      open: true,
      message: first,
      severity: typeof second === "string" ? second : "error",
      useErrorDialog: false,
    });
  }, []);

  const openStockLimitDialog = useCallback((productName, capFloor) => {
    setStockLimitDialog({
      open: true,
      productName: productName || "Product",
      maxQty: Math.max(0, Math.floor(Number(capFloor))),
      message: "",
      plainTitle: undefined,
    });
  }, []);

  const closeStockLimitDialog = useCallback(() => {
    setStockLimitDialog({
      open: false,
      productName: "",
      maxQty: 0,
      message: "",
      plainTitle: undefined,
    });
  }, []);

  const onCartErrorMessage = useCallback((message, severity = "error") => {
    const msg = String(message || "").trim();
    const isStockLimitError =
      /out of stock|insufficient|exceeds?\b|exceeded|not enough|unavailable|inventory|left in stock|only\s+\d+/i.test(
        msg
      ) ||
      /available\s+quantity|stock\s+limit|maximum\s+quantity|qty\s+reached|quantity\s+reached|left in stock|max(imum)?\s+(qty|quantity|available)|cannot\s+purchase\s+more\s+than/i.test(
        msg
      );
    if (isStockLimitError) {
      const isPurchaseLimitExceeded = /cannot\s+purchase\s+more\s+than/i.test(msg);
      setStockLimitDialog({
        open: true,
        productName: "",
        maxQty: undefined,
        message: msg || "Stock limit reached",
        plainTitle: isPurchaseLimitExceeded
          ? "Purchase Limit Exceeded"
          : "Stock limit reached",
      });
      return;
    }
    showSnackbar(message, severity);
  }, [showSnackbar]);

  const { settings: companySettings } = useCompanySettings();
  const shouldCheckStockBeforeAddToCart = resolveCheckStockBeforeAddToCart(companySettings);

  const warnStockLimitIfNeeded = useCallback(
    (sourceItem, qtyToCheck, allowDecimal) => {
      if (!shouldCheckStockBeforeAddToCart) {
        return;
      }
      if (!isQuantityWithinLineStock(sourceItem, qtyToCheck, allowDecimal)) {
        const cap = getStockCapForCartLineItem(sourceItem);
        openStockLimitDialog(
          getTelecallerProductDisplayName(sourceItem),
          cap != null ? Math.floor(cap) : 0
        );
      }
    },
    [shouldCheckStockBeforeAddToCart, openStockLimitDialog]
  );

  const [saveConfirmDialog, setSaveConfirmDialog] = useState(false);
  const [pendingSaveMode, setPendingSaveMode] = useState(null);
  const saveActionRef = useRef(null);
  const [deleteItemDialog, setDeleteItemDialog] = useState({
    open: false,
    itemId: null,
  });
  const [customerChangeDialog, setCustomerChangeDialog] = useState({
    open: false,
    newCustomer: null,
  });
  const [isBarcodeProcessing, setIsBarcodeProcessing] = useState(false);
  const [newlyAddedProductId, setNewlyAddedProductId] = useState(null);
  const [promotionsModalOpen, setPromotionsModalOpen] = useState(false);
  const [productLinePromotionsCtx, setProductLinePromotionsCtx] = useState(null);
  const [promotionDrawerOpen, setPromotionDrawerOpen] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [promotionActionPending, setPromotionActionPending] = useState(false);
  const [promotionRefreshKey, setPromotionRefreshKey] = useState(0);
  const [promoProductSwapOpen, setPromoProductSwapOpen] = useState(false);
  const [promoProductSwapCtx, setPromoProductSwapCtx] = useState(null);
  const [inventoryModalOpen, setInventoryModalOpen] = useState(false);
  const [inventoryModalAnchorPosition, setInventoryModalAnchorPosition] = useState(null);
  const isPromotionOperationRef = useRef(false);
  const productSearchRef = useRef(null);
  const pendingBarcodeInputRef = useRef("");
  const productSearchInputRef = useRef(productSearchInput);
  const selectedPriceBrandRef = useRef(selectedPriceBrand);
  const isProcessingBarcodeRef = useRef(false);
  const [quantityKeypadOpen, setQuantityKeypadOpen] = useState(false);
  const [selectedProductForQuantity, setSelectedProductForQuantity] = useState(null);
  const [keypadError, setKeypadError] = useState("");
  const [selectedOrderType, setSelectedOrderType] = useState("Box");
  const keypadOpeningRef = useRef(false);
  const quantityKeypadOpenRef = useRef(false);
  const openQuantityKeypadForProductRef = useRef(null);
  const closeQuantityKeypadRef = useRef(null);
  const selectedProductForQuantityRef = useRef(null);
  const selectedOrderTypeRef = useRef("Box");
  const cartItemsRef = useRef([]);
  const handleBarcodeScanProductRef = useRef(null);
  const applyScanQuantityRef = useRef(null);
  const lastScannedLineRef = useRef(null);
  const qtyEntryBufferRef = useRef("");
  const scanQtyShouldReplaceRef = useRef(false);
  const scanQtyApplyTimeoutRef = useRef(null);
  const cartQtyValidationRef = useRef(new Map());

  const debouncedCustomerSearch = useDebounce(customerSearchInput, 500);
  const debouncedProductSearch = useDebounce(productSearchInput, 500);
  const tillUserId = localStorage.getItem("tillUserId");

  const isTelecaller = useMemo(
    () => localStorage.getItem("user_role") === "20",
    []
  );

  const generateUniqueId = () => {
    return `id_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
  };

  const {
    cartItems,
    cartTotals,
    isLoadingCart,
    addProductToCart,
    handleQuantityChange,
    handleTypeChange,
    handleAddAsNewType,
    handlePriceChange,
    handleDiscountChange,
    handleDeleteItem,
    handleClearCart,
    getItemDetails,
    isItemPending,
    refetchCart,
    cacheProductCategory,
    cacheProductDisplay,
    bundleDiscounts = [],
    bundleLineDiscountByKey = null,
  } = useEnhancedCartLogic(
    selectedCustomer?.id,
    totalInvDiscPercent,
    PLATFORMS.POS,
    totalInvDiscType,
    onCartErrorMessage,
    isTelecaller
  );

  useEffect(() => {
    cartQtyValidationRef.current = syncCartQtyValidationMap(cartItems);
  }, [cartItems]);
  const cartItemLookup = useMemo(() => getCartItemLookupMap(cartItems), [cartItems]);

  const isPriceBrandLocked =
    cartItems.length > 0 ||
    selectedCartRowIndex !== null ||
    Boolean(selectedProductForQuantity) ||
    quantityKeypadOpen;

  useEffect(() => {
    if (cartItems.length === 0) {
      setSelectedCartRowIndex(null);
      setSelectedCartRow(null);
      return;
    }
    if (!selectedCartRow) {
      return;
    }
    let match = null;
    if (selectedCartRow.id != null) {
      match = cartItems.find(
        (item) =>
          item.id != null && String(item.id) === String(selectedCartRow.id)
      );
    }
    if (!match) {
      match = getCartItemFromLookup(
        cartItemLookup,
        selectedCartRow.product_id,
        selectedCartRow.order_type || selectedCartRow.type || "Box"
      );
    }
    if (!match) {
      setSelectedCartRowIndex(null);
      setSelectedCartRow(null);
      return;
    }
    setSelectedCartRow(match);
  }, [cartItems]);

  const handleCartRowSelectionChange = useCallback((index, item) => {
    setSelectedCartRowIndex(index);
    setSelectedCartRow(item ?? null);
    const lastScan = lastScannedLineRef.current;
    if (!lastScan || !item) {
      return;
    }
    const sameProduct =
      String(item.product_id) === String(lastScan.productId) &&
      (item.order_type || item.type || "Box") === lastScan.orderType;
    if (!sameProduct) {
      lastScannedLineRef.current = null;
      qtyEntryBufferRef.current = "";
    }
  }, []);

  const clearCartRowSelection = useCallback(() => {
    setSelectedCartRowIndex(null);
    setSelectedCartRow(null);
  }, []);

  const selectedCartRowListPrice = useMemo(() => {
    if (!selectedCartRow) {
      return 0;
    }
    return resolveRhpCartLineListPrice(selectedCartRow);
  }, [selectedCartRow]);

  const handleOpenRhpDiscountDialog = () => {
    if (!selectedCartRow) {
      showSnackbar("Please select a cart line first", "warning");
      return;
    }
    const itemDetails = getItemDetails ? getItemDetails(selectedCartRow) : null;
    if (itemDetails?.isFree) {
      showSnackbar("Cannot apply discount to free promotional items", "warning");
      return;
    }
    if (
      selectedCartRow.is_promoted === "1" ||
      selectedCartRow.is_promoted === 1 ||
      selectedCartRow.is_promoted === true
    ) {
      showSnackbar("Cannot apply discount to promotional items", "warning");
      return;
    }
    setRhpDiscountDialogOpen(true);
  };

  const handleOpenRhpPriceDialog = () => {
    if (!selectedCartRow) {
      showSnackbar("Please select a cart line first", "warning");
      return;
    }
    const itemDetails = getItemDetails ? getItemDetails(selectedCartRow) : null;
    if (itemDetails?.isFree) {
      showSnackbar("Cannot change price for free promotional items", "warning");
      return;
    }
    if (
      selectedCartRow.is_promoted === "1" ||
      selectedCartRow.is_promoted === 1 ||
      selectedCartRow.is_promoted === true
    ) {
      showSnackbar("Cannot change price for promotional items", "warning");
      return;
    }
    setRhpPriceDialogOpen(true);
  };

  const handleOpenRhpStockDialog = () => {
    if (!selectedCustomer?.id) {
      showSnackbar("Please select a customer first", "warning");
      return;
    }
    setRhpStockDialogOpen(true);
  };

  const { data: customerList, isLoading: isLoadingCustomerList } = useQuery({
    queryKey: ["customerList", debouncedCustomerSearch, tillUserId],
    queryFn: () => getCustomerList({ pattern: debouncedCustomerSearch }),
    enabled: debouncedCustomerSearch.length >= 3,
  });

  const { data: productList, isLoading: isLoadingProductList } = useQuery({
    queryKey: [
      "productList",
      debouncedProductSearch,
      selectedCustomer?.id,
      selectedPriceBrand,
      tillUserId,
    ],
    queryFn: () =>
      getProductsList({
        pattern: debouncedProductSearch,
        customer_id: selectedCustomer?.id,
        platform: "4",
        price_brand: selectedPriceBrand,
      }),
    enabled: !!selectedCustomer?.id && debouncedProductSearch.length >= 2,
    // staleTime: 1000 * 60 * 5,
    // gcTime: 1000 * 60 * 10,
  });

  const { data: incompleteOrdersData } = useQuery({
    queryKey: ["incompleteOrders", PLATFORMS.POS],
    queryFn: () =>
      getCartIncompleteOrders({ channel_id: PLATFORMS.POS }),
    enabled: openIncompleteOrderDialog,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
  });

  const { data: cartPromotionsData, isLoading: isLoadingCartPromotions } =
    useQuery({
      queryKey: ["cartPromotions", selectedCustomer?.id, promotionRefreshKey],
      queryFn: () =>
        getCartPromotions(PLATFORMS.POS, PLATFORMS.POS, selectedCustomer?.id),
      enabled: !!selectedCustomer?.id,
      staleTime: 0,
      gcTime: 0,
      refetchOnWindowFocus: false,
      refetchOnMount: "always",
    });

  const formatPromotionDate = (dateString) => {
    if (!dateString || dateString === "N/A") return dateString;
    try {
      if (dateString.includes("/")) {
        const [day, month, year] = dateString.split("/");
        const date = new Date(year, month - 1, day);
        const monthNames = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        return `${day}-${monthNames[date.getMonth()]}`;
      }
      return dateString;
    } catch {
      return dateString;
    }
  };

  const getPromotionBanner = (promotion) => {
    if (promotion.type_id === "1") return "Buy X Get Y Free";
    if (promotion.type_id === "2") return "Buy X Get Y Discount";
    if (promotion.type_id === "3") return "Buy X at Discount";
    if (promotion.type_id === "4") return "Multi Layer Special";
    if (promotion.type_id === "5") return "Group Offer";
    return promotion.type || "Special Offer";
  };

  const transformPromotionData = (promotion) => {
    const buyProducts = (promotion.buy_products || []).filter(
      (p) => p.product_name && p.product_name.trim() !== ""
    );
    const offerProducts = (
      promotion.get_products ||
      promotion.offer_products ||
      []
    ).filter((p) => p.product_name && p.product_name.trim() !== "");
    const allProducts = [...buyProducts, ...offerProducts];
    const typeLabel = getPromotionBanner(promotion);
    return {
      id: String(promotion.id ?? promotion.promotion_id ?? ""),
      title: promotion.title || promotion.name || typeLabel,
      description: promotion.description || "",
      validity:
        promotion.start_date && promotion.end_date
          ? `${formatPromotionDate(promotion.start_date)} to ${formatPromotionDate(promotion.end_date)}`
          : "N/A",
      products: allProducts.map(
        (p) =>
          p.product_name || `${p.product_code || ""} - ${p.product_name || ""}`
      ),
      banner: typeLabel,
      buyProducts,
      offerProducts,
      rawData: promotion,
    };
  };

  const appliedPromotionsRaw =
    cartPromotionsData?.data?.applied_promotions &&
      Array.isArray(cartPromotionsData.data.applied_promotions)
      ? cartPromotionsData.data.applied_promotions
      : [];
  const applicableForAppliedFallback =
    cartPromotionsData?.data?.applicable_promotions &&
      Array.isArray(cartPromotionsData.data.applicable_promotions)
      ? cartPromotionsData.data.applicable_promotions.filter(
        (p) => p.is_currently_applied === true
      )
      : [];
  const appliedPromotions = (appliedPromotionsRaw.length > 0
    ? appliedPromotionsRaw
    : applicableForAppliedFallback
  ).map(transformPromotionData);
  const applicablePromotionsFromCart =
    cartPromotionsData?.data?.applicable_promotions &&
      Array.isArray(cartPromotionsData.data.applicable_promotions)
      ? cartPromotionsData.data.applicable_promotions.map(
        transformPromotionData
      )
      : [];
  const availablePromotions = applicablePromotionsFromCart.filter((promo) => {
    const appliedIds = appliedPromotions.map((p) => String(p.id));
    return !appliedIds.includes(String(promo.id));
  });
  const promotionCount = cartPromotionsData?.data?.total_applied
    ? parseInt(cartPromotionsData.data.total_applied, 10)
    : appliedPromotions.length;
  const totalApplicable = cartPromotionsData?.data?.total_applicable
    ? parseInt(cartPromotionsData.data.total_applicable, 10)
    : availablePromotions.length + appliedPromotions.length;

  const findPromotionForItem = useCallback((item, appliedPromotionsData) => {
    if (
      !item?.product_id ||
      !appliedPromotionsData ||
      !Array.isArray(appliedPromotionsData)
    )
      return null;
    const itemProductId = String(item.product_id);
    for (const promotion of appliedPromotionsData) {
      const buyProducts = promotion.buy_products || [];
      for (const buyProduct of buyProducts) {
        const productId = String(
          buyProduct.product_id ||
          buyProduct.buy_product_id ||
          buyProduct.id ||
          ""
        );
        if (productId && productId === itemProductId)
          return String(promotion.id);
      }
    }
    return null;
  }, []);

  const belongsToAppliedPromotion = useCallback((item, promo) => {
    if (!item?.product_id || !promo) return false;
    const itemProductId = String(item.product_id);
    const rawPromo = promo.rawData || promo;
    const buyProducts = rawPromo.buy_products || promo.buyProducts || [];
    for (const buyProduct of buyProducts) {
      const productId = String(
        buyProduct.product_id ||
        buyProduct.buy_product_id ||
        buyProduct.id ||
        ""
      );
      if (productId && productId === itemProductId) return true;
    }
    return false;
  }, []);

  const getAppliedPromotionForCartItem = useCallback(
    (item) => {
      if (!item || !Array.isArray(appliedPromotions) || appliedPromotions.length === 0) {
        return null;
      }
      const rawId = item.promotion_id ?? item.promoId ?? item.promosId ?? item.promotionId;
      const idStr = rawId != null && rawId !== "" ? String(rawId).trim() : "";
      if (idStr !== "") {
        const n = Number(rawId);
        if (Number.isFinite(n) && n > 0) {
          const found = appliedPromotions.find(
            (p) =>
              Number(p?.id) === n ||
              Number(p?.rawData?.id) === n ||
              Number(p?.rawData?.promotion_id) === n
          );
          if (found) {
            return found;
          }
        }
      }
      for (const promo of appliedPromotions) {
        if (belongsToAppliedPromotion(item, promo)) {
          return promo;
        }
        const rawPromo = promo.rawData || promo;
        const getProducts =
          rawPromo.get_products ||
          rawPromo.offer_products ||
          promo.getProducts ||
          promo.offerProducts ||
          [];
        const itemPid = String(item.product_id ?? "");
        for (let i = 0; i < getProducts.length; i += 1) {
          const gp = getProducts[i];
          const pid = String(gp?.product_id ?? gp?.get_product_id ?? gp?.id ?? "");
          if (pid && pid === itemPid) {
            return promo;
          }
        }
      }
      return null;
    },
    [appliedPromotions, belongsToAppliedPromotion]
  );

  useEffect(() => {
    if (
      selectedCustomer?.id &&
      cartItems.length > 0 &&
      !isPromotionOperationRef.current
    ) {
      const timer = setTimeout(
        () => setPromotionRefreshKey((prev) => prev + 1),
        500
      );
      return () => clearTimeout(timer);
    }
    if (isPromotionOperationRef.current) {
      const timer = setTimeout(() => {
        isPromotionOperationRef.current = false;
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [cartItems.length, selectedCustomer?.id]);

  const customerOptions = customerList?.customer_details || [];
  const productOptions =
    productList?.product_details ||
    productList?.product_detials ||
    productList?.data?.product_details ||
    productList?.data?.product_detials ||
    [];

  const focusProductSearchInput = useCallback((immediate = false) => {
    const run = () => productSearchRef.current?.focus?.();
    if (immediate) {
      run();
      return;
    }
    setTimeout(run, 0);
  }, []);

  const placeOrderMutation = useMutation({
    mutationFn: placeOrder,
    onSuccess: async (data) => {
      if (data.success) {
        const saveMode = saveActionRef.current;
        saveActionRef.current = null;
        showSnackbar(data.message || "Order placed successfully", "success");

        const orderId =
          data?.data?.orderId ??
          data?.data?.order_id ??
          data?.orderId ??
          data?.order_id ??
          null;

        if (saveMode === "print" && orderId) {
          const printUrl = getRhpInvoicePrintUrl(orderId, {
            settings: companySettings,
            autoprint: true,
          });
          window.open(printUrl, "_blank");
        }

        const currentCustomerId = selectedCustomer?.id;
        if (currentCustomerId) {
          if (cartItems && cartItems.length > 0) {
            cartItems.forEach((item) => {
              if (item.product_id) {
                productSplitService.clearProductType(
                  currentCustomerId,
                  item.product_id
                );
              }
            });
          }
          await clearCartByCustomer(currentCustomerId, PLATFORMS.POS);
          localStorage.removeItem(
            `epos_totalInvDiscPercent_${currentCustomerId}`
          );
          localStorage.removeItem(`epos_totalInvDiscType_${currentCustomerId}`);
        }
        setTotalInvDiscPercent(0);
        setSelectedCustomer(null);
        setCustomerSearchInput("");
        setSelectedCartRowIndex(null);
        setSelectedCartRow(null);
        queryClient.invalidateQueries({
          queryKey: ["incompleteOrders", PLATFORMS.POS],
        });
        queryClient.invalidateQueries({ queryKey: ["cartList"] });
      } else {
        saveActionRef.current = null;
        showSnackbar(data.message || "Failed to place order", "error");
      }
    },
    onError: (error) => {
      saveActionRef.current = null;
      showSnackbar("Failed to place order. Please try again.", "error");
    },
  });

  const clearCartMutation = useMutation({
    mutationFn: ({ customerId, platform }) =>
      clearCartByCustomer(customerId, platform),
    onSuccess: async (data) => {
      if (!data?.success) {
        showSnackbar(data?.message || "Failed to clear cart", "error");
        return;
      }

      queryClient.removeQueries({
        queryKey: ["cartList"],
        exact: false,
      });

      queryClient.removeQueries({
        queryKey: ["cartPromotions"],
        exact: false,
      });

      await refetchCart();
      setSelectedCartRowIndex(null);
      setSelectedCartRow(null);
      setTotalInvDiscPercent(0);
      if (selectedCustomer?.id) {
        localStorage.removeItem(
          `epos_totalInvDiscPercent_${selectedCustomer.id}`
        );
        localStorage.removeItem(`epos_totalInvDiscType_${selectedCustomer.id}`);
      }
      setPromotionRefreshKey((prev) => prev + 1);
      queryClient.invalidateQueries({
        queryKey: ["incompleteOrders", PLATFORMS.POS],
      });
      showSnackbar(data?.message || "Cart cleared successfully", "success");
    },
    onError: () => {
      showSnackbar("Failed to clear cart", "error");
    },
  });

  useEffect(() => {
    const savedCustomer = localStorage.getItem("epos_selectedCustomer");
    const savedCustomerSearch = localStorage.getItem("epos_customerSearch");
    if (savedCustomer) {
      try {
        const customer = JSON.parse(savedCustomer);
        setSelectedCustomer(customer);
        if (savedCustomerSearch) {
          setCustomerSearchInput(savedCustomerSearch);
        }
        const customerId = customer?.id;
        if (customerId) {
          const savedDiscount = localStorage.getItem(
            `epos_totalInvDiscPercent_${customerId}`
          );
          const savedDiscountType = localStorage.getItem(
            `epos_totalInvDiscType_${customerId}`
          );
          if (savedDiscount !== null) {
            const discountValue = parseFloat(savedDiscount);
            if (!isNaN(discountValue)) {
              setTotalInvDiscPercent(discountValue);
            }
          }
          if (savedDiscountType) {
            setTotalInvDiscType(savedDiscountType);
          }
        }
      } catch (error) {
        console.error("Error loading saved customer:", error);
      }
    }
  }, []);

  useEffect(() => {
    if (selectedCustomer) {
      localStorage.setItem(
        "epos_selectedCustomer",
        JSON.stringify(selectedCustomer)
      );
      if (customerSearchInput) {
        localStorage.setItem("epos_customerSearch", customerSearchInput);
      }
    } else {
      localStorage.removeItem("epos_selectedCustomer");
      localStorage.removeItem("epos_customerSearch");
    }
  }, [selectedCustomer, customerSearchInput]);

  useEffect(() => {
    if (
      selectedCustomer?.id &&
      totalInvDiscPercent !== undefined &&
      totalInvDiscPercent !== null
    ) {
      localStorage.setItem(
        `epos_totalInvDiscPercent_${selectedCustomer.id}`,
        String(totalInvDiscPercent)
      );
    }
  }, [totalInvDiscPercent, selectedCustomer?.id]);

  useEffect(() => {
    if (selectedCustomer?.id && totalInvDiscType) {
      localStorage.setItem(
        `epos_totalInvDiscType_${selectedCustomer.id}`,
        totalInvDiscType
      );
    }
  }, [totalInvDiscType, selectedCustomer?.id]);

  useEffect(() => {
    if (!selectedCustomer?.id) {
      setTotalInvDiscPercent(0);
    } else {
      const savedDiscount = localStorage.getItem(
        `epos_totalInvDiscPercent_${selectedCustomer.id}`
      );
      const savedDiscountType = localStorage.getItem(
        `epos_totalInvDiscType_${selectedCustomer.id}`
      );
      if (savedDiscount !== null) {
        const discountValue = parseFloat(savedDiscount);
        if (!isNaN(discountValue)) {
          setTotalInvDiscPercent(discountValue);
        } else {
          setTotalInvDiscPercent(0);
        }
      } else {
        setTotalInvDiscPercent(0);
      }
      if (savedDiscountType) {
        setTotalInvDiscType(savedDiscountType);
      } else {
        setTotalInvDiscType("percentage");
      }
    }
    setProductSearchInput("");
    if (!selectedCustomer) {
      setSelectedPriceBrand("1");
      return;
    }
    setSelectedPriceBrand(
      normalizePriceBrand(
        selectedCustomer.price_brand ?? selectedCustomer.priceBrand
      )
    );
  }, [selectedCustomer]);

  useEffect(() => {
    productSearchInputRef.current = productSearchInput;
  }, [productSearchInput]);

  useEffect(() => {
    selectedPriceBrandRef.current = selectedPriceBrand;
  }, [selectedPriceBrand]);

  useEffect(() => {
    quantityKeypadOpenRef.current = quantityKeypadOpen;
  }, [quantityKeypadOpen]);

  useEffect(() => {
    selectedProductForQuantityRef.current = selectedProductForQuantity;
  }, [selectedProductForQuantity]);

  useEffect(() => {
    selectedOrderTypeRef.current = selectedOrderType;
  }, [selectedOrderType]);

  useEffect(() => {
    cartItemsRef.current = cartItems;
    const lastScan = lastScannedLineRef.current;
    if (!lastScan?.productId) {
      return;
    }
    const matchIndex = getCartItemIndexFromLookup(
      cartItems,
      lastScan.productId,
      lastScan.orderType
    );
    if (matchIndex < 0) {
      return;
    }
    const matchedItem = cartItems[matchIndex];
    setSelectedCartRowIndex(matchIndex);
    setSelectedCartRow(matchedItem);

    if (lastScan.pendingAbsoluteQty) {
      const pendingQty = lastScan.pendingAbsoluteQty;
      lastScannedLineRef.current = {
        ...lastScan,
        pendingAbsoluteQty: null,
      };
      applyScanQuantityRef.current?.(pendingQty);
    }
  }, [cartItems]);

  useEffect(() => {
    if (!selectedCustomer?.id) {
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["productList"] });
    setInventoryModalOpen(false);
    setQuantityKeypadOpen(false);
    setSelectedProductForQuantity(null);
    keypadOpeningRef.current = false;
    lastScannedLineRef.current = null;
    qtyEntryBufferRef.current = "";
    scanQtyShouldReplaceRef.current = false;
    if (scanQtyApplyTimeoutRef.current) {
      clearTimeout(scanQtyApplyTimeoutRef.current);
      scanQtyApplyTimeoutRef.current = null;
    }
  }, [selectedPriceBrand, selectedCustomer?.id, queryClient]);

  const resolveScannedProductOrderType = useCallback(
    (product) => {
      const productId = String(product?.id || "");
      const splitValue = String(product?.split || "0").trim();
      let orderType = product?.order_type;
      if (!orderType) {
        if (splitValue === "1") {
          orderType = "Piece";
        } else if (splitValue === "2") {
          const storedOrderType = productSplitService.getDefaultOrderType(
            selectedCustomer?.id,
            productId
          );
          orderType = storedOrderType || "Box";
        } else {
          orderType = "Box";
        }
      }
      return orderType || "Box";
    },
    [selectedCustomer?.id]
  );

  const addScannedProductDirectlyToCart = useCallback(
    (product, orderType) => {
      if (!product) {
        return;
      }
      cacheProductCategory(product);
      cacheProductDisplay(product);
      const productId = String(product.id || "");
      const finalOrderType = orderType || resolveScannedProductOrderType(product);
      const existingItem = getCartItemFromLookup(
        cartItems,
        productId,
        finalOrderType
      );

      if (existingItem) {
        const isExistingPromotional =
          existingItem.is_promoted === "1" &&
          (existingItem.promo_type === "GET" ||
            parseFloat(existingItem.price || 0) === 0);
        const isNewProductPromotional = product.is_promoted === "1";

        if (isExistingPromotional === isNewProductPromotional) {
          const currentQty = parseQty(existingItem, existingItem.qty);
          const maxQty = isMeatsProduct(product) ? 9999.99 : 9999;
          if (currentQty >= maxQty) {
            showSnackbar(
              `Quantity cannot exceed ${maxQty}. Please reduce quantity before adding more.`,
              "warning"
            );
            return;
          }
          if (shouldCheckStockBeforeAddToCart) {
            const sourceItem = existingItem?.originalItem || existingItem;
            const nextQty = currentQty + 1;
            warnStockLimitIfNeeded(
              sourceItem,
              nextQty,
              allowsDecimalQty(existingItem)
            );
          }
          handleQuantityChange(existingItem, 1, "increment");
          return;
        }
      }

      const productWithOrderType = {
        ...product,
        order_type: finalOrderType,
        price_brand: selectedPriceBrandRef.current,
      };
      const preparedProduct = prepareProductForCart(productWithOrderType);
      warnStockLimitIfNeeded(
        preparedProduct,
        1,
        allowsDecimalQty(preparedProduct)
      );
      addProductToCart(preparedProduct);
    },
    [
      cartItems,
      cacheProductCategory,
      cacheProductDisplay,
      resolveScannedProductOrderType,
      showSnackbar,
      shouldCheckStockBeforeAddToCart,
      warnStockLimitIfNeeded,
      openStockLimitDialog,
      handleQuantityChange,
      addProductToCart,
    ]
  );

  useEffect(() => {
    if (!selectedCustomer?.id) {
      return;
    }

    let inputTimeout = null;

    const processBarcodeScan = async (barcodeValue) => {
      if (
        isProcessingBarcodeRef.current ||
        !barcodeValue ||
        barcodeValue.trim().length === 0
      ) {
        return;
      }

      isProcessingBarcodeRef.current = true;
      const trimmedValue = barcodeValue.trim();

      try {
        const response = await getProductsList({
          pattern: trimmedValue,
          customer_id: selectedCustomer.id,
          platform: "4",
          price_brand: selectedPriceBrand,
        });

        const firstProduct = response?.product_details?.[0];
        if (firstProduct) {
          const orderType = resolveScannedProductOrderType(firstProduct);
          const productForCart = {
            ...firstProduct,
            order_type: orderType,
            price_brand: selectedPriceBrandRef.current,
          };

          await handleBarcodeScanProductRef.current?.(productForCart, orderType);
        } else {
          showSnackbar("No product found for scanned code", "warning");
        }
      } catch (error) {
        showSnackbar("Unable to fetch product for scanned code", "error");
      } finally {
        isProcessingBarcodeRef.current = false;
        pendingBarcodeInputRef.current = "";
        setProductSearchInput("");
      }
    };

    let lastKeyTime = 0;
    const BARCODE_SCAN_THRESHOLD = 50;

    const clearScanQtyEntryState = (applyPending = true) => {
      if (applyPending && qtyEntryBufferRef.current) {
        applyScanQuantityRef.current?.(qtyEntryBufferRef.current);
      }
      qtyEntryBufferRef.current = "";
      scanQtyShouldReplaceRef.current = false;
      if (scanQtyApplyTimeoutRef.current) {
        clearTimeout(scanQtyApplyTimeoutRef.current);
        scanQtyApplyTimeoutRef.current = null;
      }
    };

    const scheduleScanQtyApply = () => {
      if (scanQtyApplyTimeoutRef.current) {
        clearTimeout(scanQtyApplyTimeoutRef.current);
      }
      scanQtyApplyTimeoutRef.current = window.setTimeout(() => {
        scanQtyApplyTimeoutRef.current = null;
        const qtyStr = qtyEntryBufferRef.current;
        if (!qtyStr || qtyStr === ".") {
          return;
        }
        qtyEntryBufferRef.current = "";
        applyScanQuantityRef.current?.(qtyStr);
      }, 400);
    };

    const appendScanQtyKey = (key) => {
      const allowDecimal = Boolean(lastScannedLineRef.current?.allowDecimal);

      if (key === ".") {
        if (!allowDecimal) {
          return;
        }
        if (scanQtyShouldReplaceRef.current) {
          qtyEntryBufferRef.current = "0.";
          scanQtyShouldReplaceRef.current = false;
        } else if (!qtyEntryBufferRef.current.includes(".")) {
          qtyEntryBufferRef.current = qtyEntryBufferRef.current
            ? `${qtyEntryBufferRef.current}.`
            : "0.";
        }
        scheduleScanQtyApply();
        return;
      }

      if (scanQtyShouldReplaceRef.current) {
        qtyEntryBufferRef.current = key;
        scanQtyShouldReplaceRef.current = false;
      } else {
        const newValue = `${qtyEntryBufferRef.current}${key}`;
        const decimalIndex = newValue.indexOf(".");
        if (decimalIndex === -1) {
          if (newValue.length <= 4) {
            qtyEntryBufferRef.current = newValue;
          }
        } else if (allowDecimal) {
          const integerPart = newValue.substring(0, decimalIndex);
          const decimalPart = newValue.substring(decimalIndex + 1);
          if (integerPart.length <= 4 && decimalPart.length <= 2) {
            qtyEntryBufferRef.current = newValue;
          }
        }
      }

      scheduleScanQtyApply();
    };

    const handleKeyDown = (event) => {
      const activeElement = document.activeElement;
      const dialogs = document.querySelectorAll('[role="dialog"]');
      const isDialogOpen =
        dialogs.length > 0 &&
        Array.from(dialogs).some((dialog) => {
          const style = window.getComputedStyle(dialog);
          return style.display !== "none" && style.visibility !== "hidden";
        });

      if (isDialogOpen && !quantityKeypadOpenRef.current) {
        return;
      }

      const isInputFocused =
        activeElement?.tagName === "INPUT" ||
        activeElement?.tagName === "TEXTAREA";
      const isProductInputFocused =
        isInputFocused &&
        (activeElement?.closest('[role="combobox"]') !== null ||
          activeElement?.placeholder?.toLowerCase().includes("product") ||
          activeElement
            ?.getAttribute("aria-label")
            ?.toLowerCase()
            .includes("product"));

      if (event.key === "Enter") {
        if (qtyEntryBufferRef.current) {
          event.preventDefault();
          event.stopPropagation();
          if (scanQtyApplyTimeoutRef.current) {
            clearTimeout(scanQtyApplyTimeoutRef.current);
            scanQtyApplyTimeoutRef.current = null;
          }
          const qtyStr = qtyEntryBufferRef.current;
          qtyEntryBufferRef.current = "";
          pendingBarcodeInputRef.current = "";
          applyScanQuantityRef.current?.(qtyStr);
          return;
        }
        if (pendingBarcodeInputRef.current) {
          event.preventDefault();
          event.stopPropagation();
          processBarcodeScan(pendingBarcodeInputRef.current);
          return;
        }
      }

      if (event.key === "Backspace" && lastScannedLineRef.current) {
        event.preventDefault();
        event.stopPropagation();
        qtyEntryBufferRef.current = qtyEntryBufferRef.current.slice(0, -1);
        scanQtyShouldReplaceRef.current = false;
        if (qtyEntryBufferRef.current) {
          scheduleScanQtyApply();
        } else if (scanQtyApplyTimeoutRef.current) {
          clearTimeout(scanQtyApplyTimeoutRef.current);
          scanQtyApplyTimeoutRef.current = null;
        }
        return;
      }

      if (
        event.key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey &&
        event.key !== "Enter" &&
        event.key !== "Tab" &&
        event.key !== "Escape"
      ) {
        const currentTime = Date.now();
        const timeSinceLastKey = currentTime - lastKeyTime;
        const isRapidInput = timeSinceLastKey < BARCODE_SCAN_THRESHOLD;
        const lastScan = lastScannedLineRef.current;
        const isQtyChar =
          /^[0-9]$/.test(event.key) ||
          (event.key === "." && lastScan?.allowDecimal);

        if (quantityKeypadOpenRef.current) {
          if (pendingBarcodeInputRef.current.length > 0 || isRapidInput) {
            event.preventDefault();
            event.stopPropagation();
            pendingBarcodeInputRef.current += event.key;
            lastKeyTime = currentTime;
            if (inputTimeout) {
              clearTimeout(inputTimeout);
            }
            inputTimeout = setTimeout(() => {
              if (pendingBarcodeInputRef.current.length >= 3) {
                processBarcodeScan(pendingBarcodeInputRef.current);
              } else {
                pendingBarcodeInputRef.current = "";
              }
            }, 120);
            return;
          }

          pendingBarcodeInputRef.current = event.key;
          lastKeyTime = currentTime;
          if (inputTimeout) {
            clearTimeout(inputTimeout);
          }
          inputTimeout = setTimeout(() => {
            pendingBarcodeInputRef.current = "";
          }, 80);
          return;
        }

        if (lastScan && isQtyChar) {
          event.preventDefault();
          event.stopPropagation();
          lastKeyTime = currentTime;

          if (isRapidInput || pendingBarcodeInputRef.current.length > 0) {
            clearScanQtyEntryState(true);
            lastScannedLineRef.current = null;
            pendingBarcodeInputRef.current += event.key;
            if (inputTimeout) {
              clearTimeout(inputTimeout);
            }
            inputTimeout = setTimeout(() => {
              if (pendingBarcodeInputRef.current.length >= 3) {
                processBarcodeScan(pendingBarcodeInputRef.current);
              } else {
                pendingBarcodeInputRef.current = "";
              }
            }, 120);
            return;
          }

          pendingBarcodeInputRef.current = event.key;
          if (inputTimeout) {
            clearTimeout(inputTimeout);
          }
          inputTimeout = setTimeout(() => {
            const pending = pendingBarcodeInputRef.current;
            pendingBarcodeInputRef.current = "";
            if (!pending) {
              return;
            }
            if (pending.length >= 3) {
              clearScanQtyEntryState(true);
              lastScannedLineRef.current = null;
              processBarcodeScan(pending);
            } else {
              appendScanQtyKey(pending);
            }
          }, 120);
          return;
        }

        if (lastScan && !isQtyChar) {
          qtyEntryBufferRef.current = "";
          scanQtyShouldReplaceRef.current = false;
          if (scanQtyApplyTimeoutRef.current) {
            clearTimeout(scanQtyApplyTimeoutRef.current);
            scanQtyApplyTimeoutRef.current = null;
          }
          lastScannedLineRef.current = null;
        }

        if (!isInputFocused) {
          event.preventDefault();
          event.stopPropagation();

          pendingBarcodeInputRef.current += event.key;
          lastKeyTime = currentTime;

          if (inputTimeout) {
            clearTimeout(inputTimeout);
          }
          inputTimeout = setTimeout(() => {
            if (pendingBarcodeInputRef.current) {
              processBarcodeScan(pendingBarcodeInputRef.current);
            }
          }, 500);
        } else if (
          isProductInputFocused &&
          isRapidInput &&
          pendingBarcodeInputRef.current.length > 0
        ) {
          event.preventDefault();
          event.stopPropagation();

          pendingBarcodeInputRef.current += event.key;
          lastKeyTime = currentTime;

          if (inputTimeout) {
            clearTimeout(inputTimeout);
          }
          inputTimeout = setTimeout(() => {
            if (pendingBarcodeInputRef.current) {
              processBarcodeScan(pendingBarcodeInputRef.current);
            }
          }, 500);
        } else {
          pendingBarcodeInputRef.current = "";
          lastKeyTime = currentTime;
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);

    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
      if (inputTimeout) {
        clearTimeout(inputTimeout);
      }
      if (scanQtyApplyTimeoutRef.current) {
        clearTimeout(scanQtyApplyTimeoutRef.current);
        scanQtyApplyTimeoutRef.current = null;
      }
    };
  }, [
    selectedCustomer?.id,
    selectedPriceBrand,
    showSnackbar,
  ]);

  const updateInventoryModalAnchor = useCallback(() => {
    const inputRect = productSearchRef.current?.getBoundingClientRect?.();
    if (!inputRect) {
      return;
    }
    setInventoryModalAnchorPosition({
      top: inputRect.bottom + 8,
      left: inputRect.left,
      width: inputRect.width,
    });
  }, []);

  const handleCloseInventoryModal = useCallback(() => {
    setInventoryModalOpen(false);
    setProductSearchInput("");
    focusProductSearchInput();
  }, [focusProductSearchInput]);

  const handleProductSearchChange = useCallback(
    (event) => {
      const value = event.target.value;
      setProductSearchInput(value);
      if (!selectedCustomer?.id) {
        return;
      }
      if (hasSearchableProductQuery(value)) {
        updateInventoryModalAnchor();
        setInventoryModalOpen(true);
      }
    },
    [selectedCustomer?.id, updateInventoryModalAnchor]
  );

  const handleProductSearchKeyDown = useCallback(
    (event) => {
      if (event.key !== "Enter") {
        return;
      }
      event.preventDefault();
      if (!selectedCustomer?.id) {
        showSnackbar("Please select a customer first", "warning");
        focusProductSearchInput(true);
        return;
      }
      if (hasSearchableProductQuery(productSearchInput)) {
        updateInventoryModalAnchor();
        setInventoryModalOpen(true);
      } else {
        focusProductSearchInput(true);
      }
    },
    [
      selectedCustomer?.id,
      productSearchInput,
      showSnackbar,
      focusProductSearchInput,
      updateInventoryModalAnchor,
    ]
  );

  useEffect(() => {
    if (!inventoryModalOpen) {
      return undefined;
    }
    const refocusTimer = setTimeout(() => {
      focusProductSearchInput(true);
    }, 0);
    return () => clearTimeout(refocusTimer);
  }, [inventoryModalOpen, focusProductSearchInput]);

  const resolveProductForSelectedPriceBrand = useCallback(
    async (product) => {
      if (!product || !selectedCustomer?.id) {
        return product;
      }
      const pattern = String(
        product.product_code || product.code || product.id || ""
      ).trim();
      const priceBrand = selectedPriceBrandRef.current;
      if (!pattern) {
        return { ...product, price_brand: priceBrand };
      }
      try {
        const fresh = await fetchProductForPriceBrand({
          pattern,
          customerId: selectedCustomer.id,
          priceBrand,
        });
        if (!fresh) {
          return { ...product, price_brand: priceBrand };
        }
        return {
          ...product,
          ...fresh,
          price_brand: priceBrand,
          order_type: product.order_type || fresh.order_type,
        };
      } catch {
        return { ...product, price_brand: priceBrand };
      }
    },
    [selectedCustomer?.id]
  );

  const applyProductQuantityToCart = useCallback(
    async (
      productToAdd,
      quantity,
      orderType,
      { quantityMode = "absolute", useKeypadError = true } = {}
    ) => {
      if (!productToAdd) {
        return { ok: false };
      }

      cacheProductCategory(productToAdd);
      cacheProductDisplay(productToAdd);
      const isMeats = isMeatsProduct(productToAdd);
      const numValue = isMeats ? parseFloat(quantity) : parseInt(quantity, 10);
      const reportError = (message) => {
        if (useKeypadError) {
          setKeypadError(message);
        } else {
          showSnackbar(message, "warning");
        }
      };

      if (isNaN(numValue)) {
        reportError("Please enter a valid quantity");
        return { ok: false };
      }
      if (isMeats) {
        if (numValue < 0.01 || numValue > 9999.99) {
          reportError("Please enter a valid quantity (0.01-9999.99)");
          return { ok: false };
        }
        const decimalPart = String(quantity).split(".")[1];
        if (decimalPart && decimalPart.length > 2) {
          reportError("Quantity can have at most 2 decimal places");
          return { ok: false };
        }
      } else if (numValue < 1 || numValue > 9999) {
        reportError("Please enter a valid quantity (1-9999)");
        return { ok: false };
      }

      const finalOrderType = orderType || productToAdd.order_type || "Box";
      const productId = String(productToAdd.id || "");
      const items = cartItemsRef.current;

      if (selectedCustomer?.id && productToAdd.id) {
        const splitValue = String(productToAdd.split || "0").trim();
        if (splitValue === "2") {
          productSplitService.setDefaultOrderType(
            selectedCustomer.id,
            productToAdd.id,
            finalOrderType
          );
        }
      }

      const existingItem = getCartItemFromLookup(
        items,
        productId,
        finalOrderType
      );

      const lineForStock = existingItem
        ? existingItem?.originalItem || existingItem
        : mergeOrderLimitFieldsFromCartLines(
            { ...productToAdd, order_type: finalOrderType },
            items
          );
      const allowDec = existingItem ? allowsDecimalQty(existingItem) : isMeats;
      const currentQty = existingItem ? parseQty(existingItem, existingItem.qty) : 0;
      const targetQty =
        quantityMode === "increment" && existingItem
          ? currentQty + numValue
          : numValue;

      const purchaseLimit = existingItem
        ? validateMaximumOrderQuantityForLineQty(lineForStock, items, targetQty)
        : validateMaximumOrderQuantityForCartAdd(
            { ...productToAdd, order_type: finalOrderType },
            items,
            numValue
          );
      if (!purchaseLimit.allowed) {
        setStockLimitDialog({
          open: true,
          productName: getTelecallerProductDisplayName(lineForStock),
          maxQty: undefined,
          message: purchaseLimit.message || "",
          plainTitle: "Purchase Limit Exceeded",
        });
        return { ok: false };
      }

      if (shouldCheckStockBeforeAddToCart) {
        const stockQty = quantityMode === "increment" ? numValue : targetQty;
        warnStockLimitIfNeeded(lineForStock, stockQty, allowDec);
      }

      if (existingItem) {
        handleQuantityChange(
          existingItem,
          numValue,
          quantityMode === "increment" ? "increment" : "absolute"
        );
      } else {
        const pricedProduct = await resolveProductForSelectedPriceBrand({
          ...productToAdd,
          order_type: finalOrderType,
        });
        const productWithQuantity = {
          ...pricedProduct,
          quantity: numValue,
          qty: String(numValue),
          order_type: finalOrderType,
          price_brand: selectedPriceBrandRef.current,
        };
        const preparedProduct = prepareProductForCart(productWithQuantity);
        addProductToCart(preparedProduct);
      }

      return { ok: true };
    },
    [
      selectedCustomer?.id,
      cacheProductCategory,
      cacheProductDisplay,
      shouldCheckStockBeforeAddToCart,
      warnStockLimitIfNeeded,
      openStockLimitDialog,
      handleQuantityChange,
      addProductToCart,
      resolveProductForSelectedPriceBrand,
      showSnackbar,
    ]
  );

  const commitPendingKeypadProductWithDefaultQty = useCallback(async () => {
    const productToAdd = selectedProductForQuantityRef.current;
    if (!productToAdd) {
      return { ok: true };
    }

    const finalOrderType =
      selectedOrderTypeRef.current || productToAdd.order_type || "Box";
    const productId = String(productToAdd.id || "");
    const existingItem = getCartItemFromLookup(
      cartItemsRef.current,
      productId,
      finalOrderType
    );

    if (existingItem) {
      return { ok: true };
    }

    return applyProductQuantityToCart(productToAdd, 1, finalOrderType, {
      useKeypadError: false,
    });
  }, [applyProductQuantityToCart]);

  const resetQuantityKeypadState = useCallback(() => {
    keypadOpeningRef.current = false;
    quantityKeypadOpenRef.current = false;
    setQuantityKeypadOpen(false);
    setSelectedProductForQuantity(null);
    setKeypadError("");
    setSelectedOrderType("Box");
  }, []);

  const waitForQuantityKeypadClose = useCallback(
    () =>
      new Promise((resolve) => {
        window.setTimeout(resolve, 200);
      }),
    []
  );

  const handleProductSelectForQuantity = useCallback(async (product) => {
    if (!product || !selectedCustomer?.id || keypadOpeningRef.current) return;

    const pricedProduct = await resolveProductForSelectedPriceBrand(product);
    const productId = String(pricedProduct.id || "");
    const splitValue = String(pricedProduct.split || "0").trim();

    let defaultOrderType = pricedProduct.order_type;
    if (!defaultOrderType) {
      if (splitValue === "1") {
        defaultOrderType = "Piece";
      } else if (splitValue === "2") {
        const storedOrderType = productSplitService.getDefaultOrderType(
          selectedCustomer.id,
          productId
        );
        defaultOrderType = storedOrderType || "Box";
      } else {
        defaultOrderType = "Box";
      }
    }

    const availableQtyForType = defaultOrderType === "Piece"
      ? (parseFloat(pricedProduct.availablePieceQty ?? 0) || 0)
      : (parseFloat(pricedProduct.availableBoxQty ?? 0) || 0);
    if (shouldCheckStockBeforeAddToCart && availableQtyForType <= 0) {
      openStockLimitDialog(getTelecallerProductDisplayName(pricedProduct), 0);
    }

    const existingItem = getCartItemFromLookup(
      cartItems,
      productId,
      defaultOrderType
    );

    if (existingItem) {
      const isExistingPromotional =
        existingItem.is_promoted === "1" &&
        (existingItem.promo_type === "GET" ||
          parseFloat(existingItem.price || 0) === 0);
      const isNewProductPromotional = pricedProduct.is_promoted === "1";

      if (isExistingPromotional === isNewProductPromotional) {
        const currentQty = parseQty(existingItem, existingItem.qty);
        const maxQty = isMeatsProduct(pricedProduct) ? 9999.99 : 9999;
        if (currentQty >= maxQty) {
          showSnackbar(
            `Quantity cannot exceed ${maxQty}. Please reduce quantity before adding more.`,
            "warning"
          );
          return;
        }
        pricedProduct.qty = String(currentQty);
        pricedProduct.quantity = currentQty;
      } else {
        pricedProduct.qty = "1";
        pricedProduct.quantity = 1;
      }
    } else {
      pricedProduct.qty = "1";
      pricedProduct.quantity = 1;
    }

    keypadOpeningRef.current = true;
    lastScannedLineRef.current = null;
    qtyEntryBufferRef.current = "";
    scanQtyShouldReplaceRef.current = false;
    if (scanQtyApplyTimeoutRef.current) {
      clearTimeout(scanQtyApplyTimeoutRef.current);
      scanQtyApplyTimeoutRef.current = null;
    }
    setProductSearchInput("");
    setKeypadError("");
    setSelectedOrderType(defaultOrderType);
    setSelectedProductForQuantity({ ...pricedProduct });

    window.setTimeout(() => {
      quantityKeypadOpenRef.current = true;
      setQuantityKeypadOpen(true);
      window.setTimeout(() => {
        keypadOpeningRef.current = false;
      }, 300);
    }, 100);
  }, [selectedCustomer?.id, cartItems, showSnackbar, shouldCheckStockBeforeAddToCart, openStockLimitDialog, resolveProductForSelectedPriceBrand]);

  useEffect(() => {
    openQuantityKeypadForProductRef.current = handleProductSelectForQuantity;
  }, [handleProductSelectForQuantity]);

  const handleBarcodeScanProduct = useCallback(
    async (productForCart, orderType) => {
      const finalOrderType =
        orderType || resolveScannedProductOrderType(productForCart);
      const payload = {
        ...productForCart,
        order_type: finalOrderType,
        price_brand: selectedPriceBrandRef.current,
      };

      if (quantityKeypadOpenRef.current) {
        const committed = await commitPendingKeypadProductWithDefaultQty();
        if (!committed.ok) {
          return;
        }
        resetQuantityKeypadState();
        await waitForQuantityKeypadClose();
      }

      qtyEntryBufferRef.current = "";
      scanQtyShouldReplaceRef.current = true;
      if (scanQtyApplyTimeoutRef.current) {
        clearTimeout(scanQtyApplyTimeoutRef.current);
        scanQtyApplyTimeoutRef.current = null;
      }
      lastScannedLineRef.current = {
        productId: String(productForCart.id || ""),
        orderType: finalOrderType,
        product: payload,
        allowDecimal: isMeatsProduct(productForCart),
      };

      addScannedProductDirectlyToCart(payload, finalOrderType);
    },
    [
      resolveScannedProductOrderType,
      commitPendingKeypadProductWithDefaultQty,
      resetQuantityKeypadState,
      waitForQuantityKeypadClose,
      addScannedProductDirectlyToCart,
    ]
  );

  useEffect(() => {
    handleBarcodeScanProductRef.current = handleBarcodeScanProduct;
  }, [handleBarcodeScanProduct]);

  const applyScanQuantity = useCallback(
    async (qtyString) => {
      const lastScan = lastScannedLineRef.current;
      const trimmed = String(qtyString || "").trim();
      if (!lastScan || !trimmed || trimmed === ".") {
        return;
      }

      const existingItem = getCartItemFromLookup(
        cartItemsRef.current,
        lastScan.productId,
        lastScan.orderType
      );

      if (!existingItem) {
        lastScannedLineRef.current = {
          ...lastScan,
          pendingAbsoluteQty: trimmed,
        };
        return;
      }

      lastScannedLineRef.current = {
        ...lastScan,
        pendingAbsoluteQty: null,
      };

      const isMeats = isMeatsProduct(existingItem);
      const targetQty = isMeats ? parseFloat(trimmed) : parseInt(trimmed, 10);
      if (!Number.isFinite(targetQty) || targetQty <= 0) {
        return;
      }

      const currentQty = parseQty(existingItem, existingItem.qty);
      if (currentQty === targetQty) {
        scanQtyShouldReplaceRef.current = true;
        return;
      }

      handleQuantityChange(existingItem, trimmed, "absolute");
      scanQtyShouldReplaceRef.current = true;
      setProductSearchInput("");
    },
    [handleQuantityChange]
  );

  useEffect(() => {
    applyScanQuantityRef.current = applyScanQuantity;
  }, [applyScanQuantity]);

  const handleInventoryProductSelectForQuantity = useCallback(
    (product) => {
      setInventoryModalOpen(false);
      handleProductSelectForQuantity(product);
    },
    [handleProductSelectForQuantity]
  );

  const handleQuantityConfirmFromSearch = useCallback(async (quantity, orderType = null) => {
    const productToAdd = selectedProductForQuantity;

    if (!productToAdd) {
      resetQuantityKeypadState();
      return;
    }

    const finalOrderType = orderType || selectedOrderType || productToAdd.order_type || "Box";
    const result = await applyProductQuantityToCart(
      productToAdd,
      quantity,
      finalOrderType,
      { quantityMode: "absolute", useKeypadError: true }
    );

    if (!result.ok) {
      return;
    }

    resetQuantityKeypadState();

    setTimeout(() => {
      focusProductSearchInput();
    }, 200);
  }, [
    selectedProductForQuantity,
    selectedOrderType,
    applyProductQuantityToCart,
    resetQuantityKeypadState,
    focusProductSearchInput,
  ]);

  const handleQuantityKeypadClose = useCallback(() => {
    resetQuantityKeypadState();
  }, [resetQuantityKeypadState]);

  useEffect(() => {
    closeQuantityKeypadRef.current = handleQuantityKeypadClose;
  }, [handleQuantityKeypadClose]);

  const buildOrderPayload = () => {
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const userId = userData.id || "";
    const today = new Date().toISOString().split("T")[0];
    const jwtToken = localStorage.getItem("token");
    const accessToken = localStorage.getItem("accessToken");
    const token = jwtToken || accessToken;

    const saleDetails = cartItems.map((item, index) => {
      const orderType = item.order_type || item.type || "Box";
      const price = parseFloat(item.price) || 0;
      const unitPrice = parseFloat(item.unit_price || item.price || 0) || 0;
      const qty = parseQty(item, item.qty);
      const vatRate = parseFloat(item.tax_rate || item.rate) || 0;
      const vatAmount = parseFloat(item.vat || item.tax) || 0;

      // Get discount_type to determine how to calculate discount
      const rawDiscountType =
        item.discount_type ||
        item.manualOverrides?.discount_type ||
        "percentage";
      const normalizedDiscountType = String(rawDiscountType)
        .trim()
        .toLowerCase();
      const isFixedDiscount =
        normalizedDiscountType === "£" || normalizedDiscountType === "amount";

      const subtotal = parseFloat(item.subtotal || 0) || 0;
      const totalVat = parseFloat(item.vat || 0) || 0;

      let discountPercent = 0;
      let discountAmount = 0;
      let discountedSubtotal = subtotal;
      let discountedVat = totalVat;

      const itemDiscount = parseFloat(
        item.discount || item.manualOverrides?.discount || 0
      );

      if (isFixedDiscount) {
        discountAmount = itemDiscount;
        if (!Number.isFinite(discountAmount) || discountAmount < 0) {
          discountAmount = 0;
        }
        discountPercent = subtotal > 0 ? (discountAmount / subtotal) * 100 : 0;
        discountPercent = Math.min(discountPercent, 100);
        discountedSubtotal = Math.max(0, subtotal);
        discountedVat = totalVat;
      } else {
        discountPercent = itemDiscount;
        if (!Number.isFinite(discountPercent) || discountPercent < 0) {
          discountPercent = 0;
        }
        discountPercent = Math.min(discountPercent, 100);
        const discountMultiplier = 1 - discountPercent / 100;
        discountedSubtotal = Math.max(0, subtotal);
        discountedVat = totalVat;
        discountAmount = subtotal - discountedSubtotal;
      }

      const total = discountedSubtotal + discountedVat;

      const itemType = determineItemType(item);
      const itemOriginalPrice = getOriginalPrice(item);
      const currentPrice = getPriceForItem(item);

      let promoDiscountPercent = 0;
      let promoDiscountAmount = 0;

      const originalItem = item.originalItem || item;
      const promoDiscountFromField = parseFloat(
        item.promo_discount_percentage ||
        item.promos_disc_percentage ||
        originalItem.promo_discount_percentage ||
        originalItem.promos_disc_percentage ||
        item.promo_discount ||
        item.discount_percentage ||
        originalItem.discount_percentage ||
        0
      );

      if (promoDiscountFromField > 0) {
        promoDiscountPercent = promoDiscountFromField;
        const baseSubtotalForPromo =
          itemOriginalPrice > 0 ? itemOriginalPrice * qty : subtotal;
        promoDiscountAmount =
          (baseSubtotalForPromo * promoDiscountPercent) / 100;
      }
      // else if (
      //   itemType.isDiscounted &&
      //   itemOriginalPrice > 0 &&
      //   itemOriginalPrice > currentPrice
      // ) {
      //   const priceDifference = itemOriginalPrice - currentPrice;
      //   promoDiscountPercent = (priceDifference / itemOriginalPrice) * 100;
      //   promoDiscountAmount = priceDifference * qty;
      // }

      promoDiscountPercent = Math.max(0, Math.min(promoDiscountPercent, 100));
      promoDiscountAmount = Math.max(0, promoDiscountAmount);

      const quantityType = (item.order_type || "Box").toLowerCase();
      const isContractPriceApplied =
        item.isContractPriceApplied === "Y" ? "Y" : "N";

      // Get discount_type from item (already merged from manualOverrides in baseCartItems) for API
      let discountType = "percentage";
      if (
        rawDiscountType !== undefined &&
        rawDiscountType !== null &&
        rawDiscountType !== ""
      ) {
        if (normalizedDiscountType === "£") {
          discountType = "amount";
        } else if (normalizedDiscountType === "percentage") {
          discountType = "percentage";
        } else if (normalizedDiscountType === "amount") {
          discountType = "amount";
        }
      }

      // Get discount_value based on discount_type
      let discountValue = "0.00";
      if (discountType === "amount") {
        // For amount type, use the discount amount directly
        discountValue = discountAmount.toFixed(2);
      } else {
        // For percentage type, use the percentage value
        discountValue = discountPercent.toFixed(2);
      }

      // Get promo_id and promo_type from item (check multiple possible field names)
      const promoId = item.promo_id || item.promosId || item.promoId || null;
      const promoType = item.promo_type || item.promoType || null;

      const hasPromotion =
        promoId !== null &&
        promoId !== undefined &&
        promoId !== "" &&
        promoType !== null &&
        promoType !== undefined &&
        promoType !== "";
      const isPromoted = item.is_promoted === "1" || item.is_promoted === 1;
      const sendPromoDiscount = isPromoted && hasPromotion;

      const payload = {
        product_id: String(item.product_id || ""),
        product_code: item.code || item.product_code || "",
        product_name: item.name || item.product_name || "",
        unit_price: unitPrice.toFixed(2),
        quantity: qty,
        quantityType: quantityType,
        productWiseDiscountPercentage: discountPercent.toFixed(2),
        productWiseDiscountAmount: discountAmount.toFixed(2),
        discount_type: discountType,
        discount_value: discountValue,
        subTotal: discountedSubtotal.toFixed(2),
        vat: discountedVat.toFixed(2),
        isPromoted: isPromoted ? 1 : 0,
        promoDiscountPercentage: sendPromoDiscount
          ? promoDiscountPercent.toFixed(2)
          : "0.00",
        promoDiscountAmount: sendPromoDiscount
          ? promoDiscountAmount.toFixed(2)
          : "0.00",
        total: total.toFixed(2),
        isContractPriceApplied: isContractPriceApplied,
        is_refund: isRhpRefundCartLine(item) ? "1" : "0",
      };

      // Only include promo_id and promo_type if promotion is actually applied
      if (hasPromotion) {
        payload.promo_id = String(promoId);
        payload.promo_type = String(promoType);
      }

      return payload;
    });

    const subtotal = cartTotals?.subtotal || 0;
    const vat = cartTotals?.vat || 0;
    const totalBeforeDiscount = cartTotals?.totalBeforeDiscount || 0;
    const grandTotal = cartTotals?.grandTotal || 0;

    let invoiceWiseDiscountPercentage = "0.00";
    let invoiceWiseDiscountAmount = "0.00";

    if (totalInvDiscType === "percentage") {
      invoiceWiseDiscountPercentage = (
        parseFloat(totalInvDiscPercent) || 0
      ).toFixed(2);
      invoiceWiseDiscountAmount =
        totalBeforeDiscount > 0
          ? (
            (totalBeforeDiscount * parseFloat(totalInvDiscPercent || 0)) /
            100
          ).toFixed(2)
          : "0.00";
    } else if (totalInvDiscType === "amount") {
      invoiceWiseDiscountPercentage =
        totalBeforeDiscount > 0
          ? (
            ((parseFloat(totalInvDiscPercent) || 0) / totalBeforeDiscount) *
            100
          ).toFixed(2)
          : "0.00";
      invoiceWiseDiscountAmount = (
        parseFloat(totalInvDiscPercent) || 0
      ).toFixed(2);
    }

    return {
      platform: "4",
      authorization: `Bearer ${token}`,
      user_id: userId,
      product_count: cartItems.length,
      customer_id: String(selectedCustomer.id || ""),
      customer_address: selectedCustomer.address || "",
      totalAmount: grandTotal.toFixed(2),
      totalVAT: vat.toFixed(2),
      paymentMode: "",
      staff_note: "",
      note_for_driver: "",
      invoiceWiseDiscountPercentage: invoiceWiseDiscountPercentage,
      invoiceWiseDiscountAmount: invoiceWiseDiscountAmount,
      invoiceWiseDiscountType: totalInvDiscType,
      discountAmount: "",
      promotionId: "",
      promotionSubType: "",
      collect_amount: "0.00",
      uniqueOrderId: generateUniqueId(),
      chequeNo: "",
      chequeDate: "",
      latitude: "",
      longitude: "",
      address: "",
      product_discount: "0.00",
      status: "Incomplete",
      sales_id: "",
      reference_no: 0,
      date: today,
      sale_details: saleDetails,
    };
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
      useErrorDialog: false,
    }));
  };

  const handleBarcodeDetected = useCallback(
    async (value) => {
      if (isBarcodeProcessing) {
        return;
      }
      const trimmedValue = `${value}`.trim();
      if (!trimmedValue) {
        return;
      }
      if (!selectedCustomer?.id) {
        showSnackbar("Please select a customer before scanning", "warning");
        return;
      }
      setIsBarcodeProcessing(true);
      setProductSearchInput(trimmedValue);
      try {
        const response = await getProductsList({
          pattern: trimmedValue,
          customer_id: selectedCustomer.id,
          platform: "4",
          price_brand: selectedPriceBrand,
        });
        const firstProduct = response?.product_details?.[0];
        if (firstProduct) {
          const orderType = resolveScannedProductOrderType(firstProduct);
          const productForCart = {
            ...firstProduct,
            order_type: orderType,
            price_brand: selectedPriceBrandRef.current,
          };

          await handleBarcodeScanProductRef.current?.(productForCart, orderType);
          setProductSearchInput("");
        } else {
          showSnackbar("No product found for scanned code", "warning");
        }
      } catch (error) {
        showSnackbar("Unable to fetch product for scanned code", "error");
      } finally {
        setIsBarcodeProcessing(false);
      }
    },
    [
      isBarcodeProcessing,
      selectedCustomer,
      selectedPriceBrand,
      showSnackbar,
      resolveScannedProductOrderType,
    ]
  );

  const handleBarcodeError = useCallback(() => { }, []);

  const validateQuantity = (value, cartItem = null) => {
    if (!value || value === "") {
      showSnackbar("Please enter a valid quantity");
      return false;
    }

    const allowDecimal = cartItem ? allowsDecimalQty(cartItem) : false;
    const numValue = allowDecimal ? parseFloat(value) : Number(value);

    if (isNaN(numValue) || numValue < 0) {
      showSnackbar("Please enter a valid quantity");
      return false;
    }

    if (allowDecimal) {
      if (numValue < 0.01 || numValue > 9999.99) {
        showSnackbar("Quantity must be between 0.01 and 9999.99");
        return false;
      }
      const decimalPart = String(value).split(".")[1];
      if (decimalPart && decimalPart.length > 2) {
        showSnackbar("Quantity can have at most 2 decimal places");
        return false;
      }
    } else if (!Number.isInteger(numValue) || numValue < 1 || numValue > 9999) {
      showSnackbar("Quantity must be between 1 and 9999");
      return false;
    }

    return true;
  };

  const validateDiscount = (
    value,
    discountType = "percentage",
    cartItem = null
  ) => {
    if (value === "" || value === null || value === undefined) {
      return true;
    }

    const numValue = Number(value);

    if (isNaN(numValue)) {
      showSnackbar(
        `Please enter a valid discount ${discountType === "percentage" ? "percentage" : "amount"}`
      );
      return false;
    }

    if (numValue < 0) {
      showSnackbar("Discount cannot be negative");
      return false;
    }

    if (discountType === "percentage") {
      if (numValue > 100) {
        showSnackbar("Discount cannot exceed 100%");
        return false;
      }
    } else if (
      discountType === "amount" ||
      discountType === "£" ||
      discountType === "fixed"
    ) {
      if (cartItem) {
        const orderType = cartItem.order_type || cartItem.type || "Box";
        let unitPrice = 0;
        if (
          cartItem.unit_price !== undefined &&
          cartItem.unit_price !== null &&
          cartItem.unit_price !== ""
        ) {
          unitPrice = parseFloat(cartItem.unit_price);
        } else if (orderType === "Box") {
          unitPrice = parseFloat(cartItem.sales_box_price || 0);
        } else {
          unitPrice = parseFloat(cartItem.sales_piece_price || 0);
        }

        if (unitPrice > 0 && numValue > unitPrice) {
          showSnackbar(`Discount amount cannot be greater than unit price (£${unitPrice.toFixed(2)})`);
          return false;
        }
      }
    }

    const decimalPlaces = (value.toString().split(".")[1] || "").length;
    if (decimalPlaces > 2) {
      showSnackbar("Discount can have maximum 2 decimal places");
      return false;
    }

    return true;
  };

  const handleValidatedQuantityChange = (
    cartItem,
    value,
    changeType = "absolute"
  ) => {
    const sourceItem = cartItem?.originalItem || cartItem;
    const allowDecimal = allowsDecimalQty(sourceItem);

    if (changeType !== "increment" && changeType !== "decrement" && !validateQuantity(value, sourceItem)) {
      return false;
    }

    const result = computeCartQuantityChange({
      sourceItem,
      validationMap: cartQtyValidationRef.current,
      value,
      changeType,
      shouldCheckStockBeforeAddToCart: false,
      shouldSkipStockCheck: () => false,
      cartItems,
    });

    const accepted = applyCartQtyValidationResult(result, {
      onRange: (r) => {
        showSnackbar(`Quantity must be between ${r.minQty} and ${r.maxQty}`, "warning");
      },
      onPurchaseLimit: (r) => {
        setStockLimitDialog({
          open: true,
          productName: getTelecallerProductDisplayName(sourceItem),
          maxQty: 0,
          message: r.message || "",
          plainTitle: "Purchase Limit Exceeded",
        });
      },
    });

    if (!accepted) {
      return false;
    }

    if (shouldCheckStockBeforeAddToCart && result.ok) {
      const stockQtyToCheck = getProductQtyTotalInCart(
        cartItems,
        sourceItem,
        "qty",
        result.lineKey,
        result.finalQty
      );
      warnStockLimitIfNeeded(sourceItem, stockQtyToCheck, allowDecimal);
    }

    cartQtyValidationRef.current.set(result.lineKey, result.finalQty);

    handleQuantityChange(cartItem, value, changeType);
    if (selectedCustomer?.id) {
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['cartPromotions', selectedCustomer.id] });
      }, 400);
    }
    return true;
  };

  const handleValidatedTypeChange = async (cartItem, newType) => {
    await handleTypeChange(cartItem, newType);
  };

  const handleValidatedTotalInvDiscChange = (value) => {
    if (validateDiscount(value, totalInvDiscType)) {
      setTotalInvDiscPercent(value);
    }
  };

  const handleValidatedDiscountChange = (
    cartItem,
    value,
    discountType = "percentage"
  ) => {
    if (validateDiscount(value, discountType, cartItem)) {
      handleDiscountChange(cartItem, value, discountType);
    }
  };

  const handleApplyRhpLineDiscount = ({ discountType, discountValue }) => {
    if (!selectedCartRow) {
      return;
    }
    let value = discountValue;
    let type = discountType;
    if (discountType === "percentage") {
      const listPrice = resolveRhpCartLineListPrice(selectedCartRow);
      const pct = parseFloat(discountValue);
      if (!Number.isFinite(pct) || listPrice <= 0) {
        showSnackbar("Please enter a valid discount percentage", "warning");
        return;
      }
      value = ((listPrice * pct) / 100).toFixed(2);
      type = "amount";
    }
    if (validateDiscount(value, type, selectedCartRow)) {
      handleDiscountChange(selectedCartRow, value, type);
      setRhpDiscountDialogOpen(false);
      clearCartRowSelection();
    }
  };

  const handleApplyRhpLinePrice = (priceValue) => {
    if (!selectedCartRow) {
      return;
    }
    const parsed = parseFloat(priceValue);
    if (!Number.isFinite(parsed) || parsed < 0.01 || parsed > 99999.99) {
      showSnackbar("Price must be between £0.01 and £99,999.99", "warning");
      return;
    }
    handlePriceChange(selectedCartRow, parsed.toFixed(2), { commit: true });
    setRhpPriceDialogOpen(false);
    clearCartRowSelection();
  };

  const handleApplyRhpRefund = () => {
    if (!selectedCartRow) {
      showSnackbar("Please select a cart line first", "warning");
      return;
    }
    const itemDetails = getItemDetails ? getItemDetails(selectedCartRow) : null;
    if (itemDetails?.isFree) {
      showSnackbar("Cannot refund free promotional items", "warning");
      return;
    }
    const listPrice = resolveRhpCartLineListPrice(selectedCartRow);
    const absPrice = Math.abs(listPrice);
    if (!Number.isFinite(absPrice) || absPrice < 0.01) {
      showSnackbar("Invalid price for refund", "warning");
      return;
    }
    const refundPrice = (-absPrice).toFixed(2);
    handlePriceChange(selectedCartRow, refundPrice, {
      commit: true,
      allowNegative: true,
      isRefund: true,
    });
    clearCartRowSelection();
  };

  const handleDeleteItemClick = (productId) => {
    setDeleteItemDialog({ open: true, itemId: productId });
  };

  const handleConfirmDeleteItem = () => {
    if (deleteItemDialog.itemId) {
      handleDeleteItem(deleteItemDialog.itemId);
      if (selectedCustomer?.id) {
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ['cartPromotions', selectedCustomer.id] });
        }, 400);
      }
    }
    setDeleteItemDialog({ open: false, itemId: null });
  };

  const handleCloseDeleteItemDialog = () => {
    setDeleteItemDialog({ open: false, itemId: null });
  };

  const handleCancel = () => {
    handleClearCart();
    setTotalInvDiscPercent(0);
    if (selectedCustomer?.id) {
      localStorage.removeItem(
        `epos_totalInvDiscPercent_${selectedCustomer.id}`
      );
      localStorage.removeItem(`epos_totalInvDiscType_${selectedCustomer.id}`);
    }
    setSelectedCustomer(null);
    setCustomerSearchInput("");
    setPromotionRefreshKey((prev) => prev + 1);
    showSnackbar("Order cancelled", "info");
  };

  const handleClearSelectedCustomerCart = () => {
    const customerId = selectedCustomer?.id;
    if (!customerId) {
      showSnackbar("Please select a customer", "warning");
      return;
    }

    clearCartMutation.mutate({
      customerId,
      platform: PLATFORMS.POS,
    });
  };

  const handleSave = (mode = "takeNew") => {
    if (!selectedCustomer) {
      showSnackbar("Please select a customer", "warning");
      return;
    }

    if (cartItems.length === 0) {
      showSnackbar("Please add products to cart", "warning");
      return;
    }

    setPendingSaveMode(mode);
    setSaveConfirmDialog(true);
  };

  const handleSaveAndPrint = () => handleSave("print");
  const handleSaveAndTakeNew = () => handleSave("takeNew");

  const handleSaveForLater = () => {
    if (!selectedCustomer) {
      showSnackbar("Please select a customer", "warning");
      return;
    }
    setSelectedCustomer(null);
    setCustomerSearchInput("");
    setSelectedCartRowIndex(null);
    setSelectedCartRow(null);
    setSelectedPriceBrand("1");
    setProductSearchInput("");
    setTotalInvDiscPercent(0);
    setPromotionRefreshKey((prev) => prev + 1);
    localStorage.removeItem("epos_selectedCustomer");
    localStorage.removeItem("epos_customerSearch");
    showSnackbar("Order saved for later", "success");
  };

  const ensureStockBeforePlaceOrder = useCallback(async () => {
    if (!shouldCheckStockBeforeAddToCart) {
      return { ok: true };
    }
    try {
      const stockMap = await fetchProductStockMapForCartLines({
        cartItems,
        customerId: selectedCustomer?.id,
        platform: PLATFORMS.POS,
        useSearchIndex: false,
      });
      return validateCartStockBeforePlaceOrder(cartItems, stockMap, {
        shouldCheckStock: true,
        shouldSkipStockCheck: () => false,
      });
    } catch {
      showSnackbar("Unable to verify stock availability", "error");
      return { ok: false, failedFetch: true };
    }
  }, [
    cartItems,
    selectedCustomer?.id,
    shouldCheckStockBeforeAddToCart,
    showSnackbar,
  ]);

  const handleConfirmSave = async () => {
    const stockCheck = await ensureStockBeforePlaceOrder();
    if (!stockCheck.ok) {
      if (stockCheck.failedFetch) {
        setSaveConfirmDialog(false);
        setPendingSaveMode(null);
        return;
      }
      openStockLimitDialog(
        stockCheck.productName || getTelecallerProductDisplayName(stockCheck.item),
        stockCheck.cap ?? 0
      );
      setSaveConfirmDialog(false);
      setPendingSaveMode(null);
      return;
    }
    saveActionRef.current = pendingSaveMode || "takeNew";
    const orderPayload = buildOrderPayload();
    placeOrderMutation.mutate(orderPayload);
    setSaveConfirmDialog(false);
    setPendingSaveMode(null);
  };

  const handleCloseSaveConfirmDialog = () => {
    setSaveConfirmDialog(false);
    setPendingSaveMode(null);
  };

  const handleCustomerChange = (newCustomer) => {
    if (selectedCustomer && cartItems.length > 0) {
      setCustomerChangeDialog({
        open: true,
        newCustomer: newCustomer,
      });
    } else {
      setSelectedCustomer(newCustomer);
    }
  };

  const handleProceedCustomerChange = () => {
    setSelectedCustomer(customerChangeDialog.newCustomer);
    setCustomerChangeDialog({ open: false, newCustomer: null });
  };

  const handleSaveAndChangeCustomer = async () => {
    const stockCheck = await ensureStockBeforePlaceOrder();
    if (!stockCheck.ok) {
      if (stockCheck.failedFetch) return;
      openStockLimitDialog(
        stockCheck.productName || getTelecallerProductDisplayName(stockCheck.item),
        stockCheck.cap ?? 0
      );
      return;
    }
    const orderPayload = buildOrderPayload();
    placeOrderMutation.mutate(orderPayload);
    setSelectedCustomer(customerChangeDialog.newCustomer);
    setCustomerChangeDialog({ open: false, newCustomer: null });
  };

  const handleCancelCustomerChange = () => {
    setCustomerChangeDialog({ open: false, newCustomer: null });
  };

  const handleOpenPromotionsModal = () => setPromotionsModalOpen(true);
  const handleClosePromotionsModal = () => setPromotionsModalOpen(false);
  const handleOpenPromotionDrawer = (promo) => {
    setSelectedPromotion(promo);
    setPromotionDrawerOpen(true);
    setPromotionsModalOpen(false);
  };
  const handleClosePromotionDrawer = () => {
    setPromotionDrawerOpen(false);
    setSelectedPromotion(null);
  };

  const handleOpenPromotionsModalFromCartLine = useCallback(
    (lineItem) => {
      const pid = lineItem?.product_id ?? lineItem?.productId;
      if (!selectedCustomer?.id) {
        showSnackbar("Please select a customer first", "error");
        return;
      }
      if (pid === undefined || pid === null || String(pid).trim() === "") {
        showSnackbar("Product is required", "warning");
        return;
      }
      const label =
        lineItem?.desc ||
        lineItem?.name ||
        lineItem?.product_name ||
        lineItem?.description ||
        "";
      const productIds =
        collectProductIdsSharingPromotionWithLine(cartItems, lineItem);
      const productIdList =
        productIds.length > 0 ? productIds : [String(pid)];
      setProductLinePromotionsCtx({ productId: productIdList, title: label, lineItem });
    },
    [selectedCustomer?.id, showSnackbar, cartItems]
  );

  const productLinePromotionsFallback = useMemo(() => {
    if (!productLinePromotionsCtx?.lineItem) {
      return [];
    }
    const freshLineItem =
      resolveFreshCartLineForPromotionPopup(cartItems, productLinePromotionsCtx.lineItem) ??
      productLinePromotionsCtx.lineItem;
    return buildCartLinePromotionsFallback(
      freshLineItem,
      appliedPromotions,
      [...appliedPromotions, ...availablePromotions]
    );
  }, [productLinePromotionsCtx, cartItems, appliedPromotions, availablePromotions]);

  const handleCloseProductLinePromotions = useCallback(() => {
    setProductLinePromotionsCtx(null);
  }, []);

  const handleApplyPromotion = useCallback(
    async (promo) => {
      if (!selectedCustomer?.id) {
        showSnackbar("Please select a customer first", "error");
        return;
      }
      if (cartItems.length === 0) {
        showSnackbar("Please add products to cart first", "error");
        return;
      }
      const promotionId =
        promo?.id ?? promo?.rawData?.id ?? promo?.rawData?.promotion_id;
      const promotionIdStr = promotionId != null ? String(promotionId) : "";
      if (!promotionIdStr) {
        showSnackbar("Invalid promotion", "error");
        return;
      }
      setPromotionActionPending(true);
      isPromotionOperationRef.current = true;
      try {
        const storageKey = `cartManualOverrides_${selectedCustomer.id}`;
        if (typeof window !== "undefined" && storageKey)
          localStorage.removeItem(storageKey);

        const appliedPromotionsFromAPI =
          cartPromotionsData?.data?.applied_promotions &&
            Array.isArray(cartPromotionsData.data.applied_promotions)
            ? cartPromotionsData.data.applied_promotions
            : [];
        const applicablePromotionsFromAPI =
          cartPromotionsData?.data?.applicable_promotions &&
            Array.isArray(cartPromotionsData.data.applicable_promotions)
            ? cartPromotionsData.data.applicable_promotions
            : [];
        const appliedPromotionsData =
          appliedPromotionsFromAPI.length > 0
            ? appliedPromotionsFromAPI
            : applicablePromotionsFromAPI.filter(
              (p) => p.is_currently_applied === true
            );

        const previousPromotionId = resolvePreviousPromotionIdForChange({
          targetPromotionId: promotionIdStr,
          targetPromotion: promo,
          appliedPromotions: appliedPromotionsData,
          cartItems,
        });

        const response = await changePromotion({
          customerId: selectedCustomer.id,
          previousPromotionId,
          promotionId: promotionIdStr,
          channel: PLATFORMS.POS,
        });

        const success =
          response?.success === true || response?.data?.success === true;
        if (!success) {
          showSnackbar(
            response?.message ||
            response?.data?.message ||
            "Failed to apply promotion",
            "error"
          );
          return;
        }

        queryClient.removeQueries({ queryKey: ["cartList"], exact: false });
        queryClient.removeQueries({
          queryKey: ["cartPromotions"],
          exact: false,
        });
        await new Promise((resolve) => setTimeout(resolve, 300));
        await refetchCart();
        setPromotionRefreshKey((prev) => prev + 1);
        showSnackbar(
          `Promotion "${promo.title || "Promotion"}" applied successfully`,
          "success"
        );
      } catch {
        showSnackbar("Failed to apply promotion. Please try again.", "error");
        queryClient.removeQueries({ queryKey: ["cartList"], exact: false });
        queryClient.removeQueries({
          queryKey: ["cartPromotions"],
          exact: false,
        });
        await new Promise((resolve) => setTimeout(resolve, 300));
        await refetchCart();
      } finally {
        setPromotionActionPending(false);
        setTimeout(() => {
          isPromotionOperationRef.current = false;
        }, 1000);
      }
    },
    [
      selectedCustomer?.id,
      cartItems,
      cartPromotionsData?.data?.applied_promotions,
      showSnackbar,
      queryClient,
      refetchCart,
    ]
  );

  const handleRemovePromotion = useCallback(
    (promo) => {
      if (!selectedCustomer?.id) {
        showSnackbar("Please select a customer first", "error");
        return;
      }
      const promotionId =
        promo?.id ?? promo?.rawData?.id ?? promo?.rawData?.promotion_id;
      const promotionIdStr = promotionId != null ? String(promotionId) : "";
      if (!promotionIdStr) return;
      setPromotionActionPending(true);
      isPromotionOperationRef.current = true;
      remove_promotions({}, selectedCustomer.id, PLATFORMS.POS, promotionIdStr)
        .then((data) => {
          const success =
            data?.success === true || data?.data?.success === true;
          if (success) {
            showSnackbar("Promotion removed successfully", "success");
            queryClient.removeQueries({ queryKey: ["cartList"], exact: false });
            queryClient.removeQueries({
              queryKey: ["cartPromotions"],
              exact: false,
            });
            refetchCart().then(() =>
              setPromotionRefreshKey((prev) => prev + 1)
            );
          } else {
            showSnackbar(
              data?.message ||
              data?.data?.message ||
              "Failed to remove promotion",
              "error"
            );
          }
        })
        .catch(() => showSnackbar("Failed to remove promotion", "error"))
        .finally(() => {
          setPromotionActionPending(false);
          setTimeout(() => {
            isPromotionOperationRef.current = false;
          }, 1000);
        });
    },
    [selectedCustomer?.id, showSnackbar, queryClient, refetchCart]
  );

  const incompleteOrders =
    incompleteOrdersData?.data?.map((order) => ({
      id: order.id ?? order.order_id ?? null,
      customerId: order.customer_id ?? order.customerId ?? order.cust_id ?? null,
      customerName: order.name ?? order.customer_name ?? "N/A",
      date: order.created_at ?? order.date ?? "N/A",
      account_no: order.account_no ?? "",
      username: order.user_name,
    })) || [];

  const filteredIncompleteOrders = incompleteOrders.filter((order) =>
    order.customerName
      ?.toLowerCase()
      .includes(searchIncompleteOrders.toLowerCase())
  );
  

  const handleIncompleteOrder = () => {
    setOpenIncompleteOrderDialog(true);
  };

  const handleCloseIncompleteOrderDialog = () => {
    setOpenIncompleteOrderDialog(false);
  };

  const handleSelectIncompleteOrderCustomer = (order) => {
    const customerName = order.customerName || "";
    const accountNo = order.account_no || "";
    const customer = {
      id: order.customerId,
      company: customerName,
      name: customerName,
      account_no: accountNo,
    };

    setSelectedCustomer(customer);
    setCustomerSearchInput(formatCustomerDisplayLabel(customer));
    setSelectedCartRowIndex(null);
    setSelectedCartRow(null);
    setOpenIncompleteOrderDialog(false);
  };

  const rhpToolbarBtnBaseSx = {
    flex: "0 0 auto",
    whiteSpace: "nowrap",
    height: "35px !important",
    minHeight: "35px",
    maxHeight: "35px",
    py: 0,
    lineHeight: 1.2,
    boxSizing: "border-box",
    fontSize: "0.8125rem",
  };

  const rhpToolbarPrimaryBtnSx = {
    ...rhpToolbarBtnBaseSx,
    px: 1.75,
    minWidth: "96px",
  };

  const rhpToolbarBtnSx = {
    ...rhpToolbarBtnBaseSx,
    px: 1.25,
    minWidth: "72px",
  };

  const rhpLineActionDisabled =
    selectedCartRowIndex === null || cartItems.length === 0;
  const rhpLineActionDisabledTooltip =
    cartItems.length === 0
      ? "Add a product to the cart first"
      : "Select a cart line first";

  return (
    <>
      <Box sx={{ backgroundColor: "#F8F8F8", p: 1 }}>
        <Paper variant="elevation" elevation={2} sx={{ px: 2, pt: 2, pb: 1 }}>
          <Grid container spacing={1} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: "flex", alignItems: "center" }}>
              <CustomerSearch
                selectedCustomer={selectedCustomer}
                onCustomerChange={handleCustomerChange}
                searchInput={customerSearchInput}
                onSearchInputChange={setCustomerSearchInput}
                options={customerOptions}
                isLoading={isLoadingCustomerList}
                debouncedSearch={debouncedCustomerSearch}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }} sx={{ display: "flex", alignItems: "center" }}>
              <FormControl fullWidth size="small" disabled={!selectedCustomer?.id || isPriceBrandLocked} sx={{ m: 0 }}>
                <InputLabel
                  id="rhp-price-brand-label"
                  sx={{
                    top: "-8px",
                    color: "#000 !important",
                    "&.MuiInputLabel-shrink": { top: "0 !important" },
                    "&.Mui-disabled": { color: "#000 !important" },
                  }}
                >
                  P.B
                </InputLabel>
                <Select
                  labelId="rhp-price-brand-label"
                  label="P.B"
                  value={selectedPriceBrand}
                  onChange={(event) => setSelectedPriceBrand(event.target.value)}
                  sx={{
                    height: "35px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#000",
                    },
                    "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#000",
                    },
                    "&.Mui-disabled .MuiSelect-select": {
                      color: "#000 !important",
                      WebkitTextFillColor: "#000 !important",
                    },
                    "&.Mui-disabled .MuiSvgIcon-root": {
                      color: "#000 !important",
                    },
                  }}
                >
                  {PRICE_BRAND_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }} sx={{ display: "flex", alignItems: "center" }}>
              <TextField
                className="input-border"
                placeholder={!selectedCustomer?.id ? "Select customer first" : "Product"}
                inputRef={productSearchRef}
                value={productSearchInput}
                onChange={handleProductSearchChange}
                onKeyDown={handleProductSearchKeyDown}
                fullWidth
                size="small"
                disabled={!selectedCustomer?.id}
                autoComplete="off"
                margin="none"
                InputLabelProps={{ shrink: false }}
                sx={{
                  m: 0,
                  "& .MuiInputBase-root": {
                    height: "35px",
                    alignItems: "center",
                  },
                  "& .MuiInputBase-input": {
                    py: 0,
                    height: "35px",
                    boxSizing: "border-box",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#000",
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 5 }} sx={{ display: "flex", alignItems: "center" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: { xs: "wrap", lg: "nowrap" },
                  gap: 1,
                  alignItems: "center",
                  justifyContent: "flex-start",
                  width: "100%",
                  minHeight: "35px",
                  minWidth: 0,
                }}
              >
                <Button
                  variant="outlined"
                  className="button-variant-outlined"
                  onClick={handleIncompleteOrder}
                  disabled={clearCartMutation.isPending}
                  sx={rhpToolbarPrimaryBtnSx}
                >
                  Open Order
                </Button>

                <Button
                  variant="outlined"
                  className="button-variant-outlined"
                  onClick={handleClearSelectedCustomerCart}
                  disabled={clearCartMutation.isPending}
                  sx={rhpToolbarPrimaryBtnSx}
                >
                  {clearCartMutation.isPending ? "Clearing…" : "Clear Cart"}
                </Button>

                <Button
                  variant="outlined"
                  className="button-variant-outlined"
                  onClick={handleOpenRhpStockDialog}
                  disabled={!selectedCustomer?.id}
                  sx={rhpToolbarBtnSx}
                >
                  Stock
                </Button>

                <Tooltip
                  title={
                    rhpLineActionDisabled
                      ? rhpLineActionDisabledTooltip
                      : "Change price for selected line"
                  }
                  arrow
                  placement="top"
                >
                  <Box component="span" sx={{ display: "inline-flex" }}>
                    <Button
                      variant="outlined"
                      className="button-variant-outlined"
                      onClick={handleOpenRhpPriceDialog}
                      disabled={rhpLineActionDisabled}
                      sx={rhpToolbarBtnSx}
                    >
                      Price
                    </Button>
                  </Box>
                </Tooltip>
                <Tooltip
                  title={
                    rhpLineActionDisabled
                      ? rhpLineActionDisabledTooltip
                      : "Apply discount to selected line"
                  }
                  arrow
                  placement="top"
                >
                  <Box component="span" sx={{ display: "inline-flex" }}>
                    <Button
                      variant="outlined"
                      className="button-variant-outlined"
                      onClick={handleOpenRhpDiscountDialog}
                      disabled={rhpLineActionDisabled}
                      sx={rhpToolbarBtnSx}
                    >
                      Discount
                    </Button>
                  </Box>
                </Tooltip>
                <Tooltip
                  title={
                    rhpLineActionDisabled
                      ? rhpLineActionDisabledTooltip
                      : "Apply refund price to selected line"
                  }
                  arrow
                  placement="top"
                >
                  <Box component="span" sx={{ display: "inline-flex" }}>
                    <Button
                      variant="outlined"
                      className="button-variant-outlined"
                      onClick={handleApplyRhpRefund}
                      disabled={rhpLineActionDisabled}
                      sx={rhpToolbarBtnSx}
                    >
                      Refund
                    </Button>
                  </Box>
                </Tooltip>

                {promotionCount > 0 ? (
                  <>
                    <Box
                      sx={{
                        width: "1px",
                        height: 28,
                        bgcolor: "#bdbdbd",
                        flexShrink: 0,
                        mx: 0.25,
                      }}
                    />
                    <PromotionSummaryCard
                      promotionCount={promotionCount}
                      totalApplicable={totalApplicable}
                      onViewClick={handleOpenPromotionsModal}
                      disabled={!selectedCustomer?.id}
                      hideWhenNotApplied
                      showTrailingDivider
                    />
                  </>
                ) : null}
              </Box>

            </Grid>

            <Grid size={{ xs: 12, md: 12 }}>
              <CartTable
                cartItems={cartItems}
                onTypeChange={handleValidatedTypeChange}
                onAddAsNewType={handleAddAsNewType}
                onQuantityChange={handleValidatedQuantityChange}
                onDiscountChange={handleValidatedDiscountChange}
                onDiscountChangeComplete={handleValidatedDiscountChange}
                onDeleteItem={handleDeleteItemClick}
                isLoading={isLoadingCart}
                getItemDetails={getItemDetails}
                isItemPending={isItemPending}
                onValidationError={showSnackbar}
                customerId={selectedCustomer?.id}
                newlyAddedProductId={newlyAddedProductId}
                enableProductSplit={true}
                rhpCompactColumns={true}
                selectedRowIndex={selectedCartRowIndex}
                onCartRowIndexChange={handleCartRowSelectionChange}
                onQuantityPopupOpen={() => { }}
                onQuantityPopupClose={() => {
                  setNewlyAddedProductId(null);
                  setTimeout(() => {
                    const activeElement = document.activeElement;
                    const dialogs =
                      document.querySelectorAll('[role="dialog"]');
                    const isDialogOpen =
                      dialogs.length > 0 &&
                      Array.from(dialogs).some((dialog) => {
                        const style = window.getComputedStyle(dialog);
                        return (
                          style.display !== "none" &&
                          style.visibility !== "hidden"
                        );
                      });

                    const isInputInDialog =
                      activeElement?.closest('[role="dialog"]') !== null;

                    if (
                      !isDialogOpen &&
                      !isInputInDialog &&
                      productSearchRef.current
                    ) {
                      productSearchRef.current.focus();
                    }
                  }, 600);
                }}
                onPromoQtyOpenPromotionsModal={handleOpenPromotionsModalFromCartLine}
                onPromoQtyLineOpenDrawer={(cartItem) => {
                  if (!cartLineHasPromoOfferQtyControls(cartItem)) return;
                  const promo = getAppliedPromotionForCartItem(cartItem);
                  let pid = promo?.id != null ? Number(promo.id) : 27;
                  if (!Number.isFinite(pid) || pid <= 0) {
                    pid = 27;
                  }
                  setPromoProductSwapCtx({ cartItem, promotionId: pid });
                  setPromoProductSwapOpen(true);
                }}
                bundleDiscounts={bundleDiscounts}
                bundleLineDiscountByKey={bundleLineDiscountByKey}
                isTelecaller={isTelecaller}
              />
              <CartSummary
                cartTotals={cartTotals}
                totalInvDiscPercent={totalInvDiscPercent}
                onTotalInvDiscChange={handleValidatedTotalInvDiscChange}
                totalInvDiscType={totalInvDiscType}
                onTotalInvDiscTypeChange={setTotalInvDiscType}
                showTotalQtyOnly={true}
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "stretch", sm: "center" },
                  gap: 1,
                  justifyContent: "flex-end",
                  pt: 1,
                }}
              >
                <RhpActionButtons
                  onSaveAndPrint={handleSaveAndPrint}
                  onSaveAndTakeNew={handleSaveAndTakeNew}
                  onSaveForLater={handleSaveForLater}
                  isLoading={placeOrderMutation.isPending}
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      <Dialog
        open={openIncompleteOrderDialog}
        onClose={handleCloseIncompleteOrderDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1.5,
          }}
        >
          <Typography variant="h6">Open Order</Typography>
          <Box sx={{ width: "400px" }}>
            <TextField
              className="form-input-border"
              placeholder="Search by customer name"
              fullWidth
              size="small"
              value={searchIncompleteOrders}
              onChange={(e) => setSearchIncompleteOrders(e.target.value)}
              sx={{
                '& .MuiInputBase-input::placeholder': {
                  color: '#000',
                  opacity: 1,      
                  fontWeight: 500,
                },
              }}
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" className="table-cell-border">
              <TableHead >
                <TableRow className="table-cell-header">
                  <TableCell sx={{ fontWeight: 600, width: "50px" }}>
                    Sr. No.
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, width: "70px" }}>
                    Date
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, width: "250px" }}>
                    Customer Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, width: "100px" }}>
                    Order By
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredIncompleteOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No incomplete orders found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredIncompleteOrders.map((order, index) => (
                    <TableRow key={order.id || index} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{order.date}</TableCell>
                      <TableCell>
                        <Button
                          variant="text"
                          onClick={() =>
                            handleSelectIncompleteOrderCustomer(order)
                          }
                          sx={{
                            p: 0,
                            minWidth: 0,
                            textTransform: "none",
                            textDecoration: "underline",
                            justifyContent: "flex-start",
                          }}
                        >
                          {order.customerName}{" "}
                          {order.account_no ? `(${order.account_no})` : ""}
                        </Button>
                      </TableCell>
                      <TableCell>{order.username}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", my: 2 }}
          >
            <Button
              variant="outlined"
              className="button-variant-outlined"
              onClick={handleCloseIncompleteOrderDialog}
            >
              Close
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <PromotionsDialog
        open={promotionsModalOpen}
        onClose={handleClosePromotionsModal}
        appliedPromotions={appliedPromotions}
        availablePromotions={availablePromotions}
        isLoading={isLoadingCartPromotions}
        isActionPending={promotionActionPending}
        totalApplicable={totalApplicable}
        onOpenDrawer={handleOpenPromotionDrawer}
        onRemovePromotion={handleRemovePromotion}
        onApplyPromotion={handleApplyPromotion}
      />

      <ProductLinePromotionsDialog
        open={Boolean(productLinePromotionsCtx)}
        onClose={handleCloseProductLinePromotions}
        productId={productLinePromotionsCtx?.productId ?? ""}
        customerId={selectedCustomer?.id}
        productTitle={productLinePromotionsCtx?.title ?? ""}
        platform={PLATFORMS.POS}
        channels={PLATFORMS.POS}
        cartItems={cartItems}
        lineItem={productLinePromotionsCtx?.lineItem ?? null}
        fallbackPromotions={productLinePromotionsFallback}
        onApplyPromotion={handleApplyPromotion}
      />

      <PromotionDetailsDrawer
        open={promotionDrawerOpen}
        onClose={handleClosePromotionDrawer}
        promotion={selectedPromotion}
        selectedCustomer={selectedCustomer}
        platform={PLATFORMS.POS}
      />

      <PromotionGetProductsDialog
        open={promoProductSwapOpen}
        onClose={() => {
          setPromoProductSwapOpen(false);
          setPromoProductSwapCtx(null);
        }}
        cartItem={promoProductSwapCtx?.cartItem ?? null}
        promotionId={promoProductSwapCtx?.promotionId ?? 27}
        channels={5}
        platform={PLATFORMS.POS}
        customerId={selectedCustomer?.id}
        localCartItems={cartItems}
        bundleDiscounts={bundleDiscounts}
        onSwapSuccess={async () => {
          await refetchCart();
          if (selectedCustomer?.id) {
            queryClient.invalidateQueries({
              queryKey: ["cartPromotions", selectedCustomer.id],
            });
          }
          showSnackbar("Promotion product updated", "success");
        }}
      />

      <RhpLineDiscountDialog
        open={rhpDiscountDialogOpen}
        onClose={() => setRhpDiscountDialogOpen(false)}
        onApply={handleApplyRhpLineDiscount}
        listPrice={selectedCartRowListPrice}
      />

      <RhpStockDialog
        open={rhpStockDialogOpen}
        onClose={() => setRhpStockDialogOpen(false)}
        customerId={selectedCustomer?.id}
        onNotify={showSnackbar}
      />

      <RhpLinePriceDialog
        open={rhpPriceDialogOpen}
        onClose={() => setRhpPriceDialogOpen(false)}
        onApply={handleApplyRhpLinePrice}
        currentPrice={selectedCartRowListPrice}
      />

      <SalesStockLimitDialog
        open={stockLimitDialog.open}
        onClose={closeStockLimitDialog}
        productName={stockLimitDialog.productName}
        maxQty={stockLimitDialog.maxQty}
        message={stockLimitDialog.message || ""}
        plainTitle={stockLimitDialog.plainTitle}
      />

      <Snackbar
        open={Boolean(snackbar.open && !snackbar.useErrorDialog)}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ zIndex: 2000 }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        maxWidth="sm"
        open={customerChangeDialog.open}
        onClose={handleCancelCustomerChange}
        title="Unsaved Products in Cart"
        content={
          <Typography>
            You have products in the cart that haven't been saved. These items
            will be stored as an incomplete order and can be accessed later from
            the Incomplete Orders section.
          </Typography>
        }
        action={
          <>
            <Button
              variant="contained"
              color="error"
              onClick={handleSaveAndChangeCustomer}
              sx={{ mr: 1 }}
              disabled={placeOrderMutation.isPending}
            >
              {placeOrderMutation.isPending
                ? "Saving..."
                : "Save Current Order"}
            </Button>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#F8991D",
                "&:hover": {
                  backgroundColor: "#F8991D",
                  color: "#fff",
                },
              }}
              onClick={handleProceedCustomerChange}
            >
              Change Customer
            </Button>
          </>
        }
      />

      <ConfirmDialog
        open={saveConfirmDialog}
        onClose={handleCloseSaveConfirmDialog}
        title="Confirm Order"
        content={
          <Typography>
            {pendingSaveMode === "print"
              ? "Place this order and print the invoice?"
              : "Are you sure you want to place this order?"}
          </Typography>
        }
        action={
          <>
            <Button
              variant="contained"
              onClick={handleConfirmSave}
              disabled={placeOrderMutation.isPending}
              sx={{
                ml: 1,
                backgroundColor: "#F8991D",
                "&:hover": {
                  backgroundColor: "#F8991D",
                  color: "#fff",
                },
              }}
            >
              {placeOrderMutation.isPending ? "Placing Order..." : "Confirm"}
            </Button>
          </>
        }
      />

      <ConfirmDialog
        open={deleteItemDialog.open}
        onClose={handleCloseDeleteItemDialog}
        title="Delete Item"
        content={
          <>
            <Typography variant="body1" sx={{ mb: 1.5 }}>
              Are you sure you want to remove this product from your cart? This
              action cannot be undone.
            </Typography>
            {(() => {
              const itemToDelete = cartItems.find((item) => {
                const itemId = item.id || item.product_id;
                return String(itemId) === String(deleteItemDialog.itemId);
              });

              if (!itemToDelete) return null;

              const itemDetails = getItemDetails
                ? getItemDetails(itemToDelete)
                : null;
              const itemHasPromotions =
                itemToDelete.is_promoted === "1" ||
                (itemToDelete.promo_id !== null &&
                  itemToDelete.promo_id !== undefined &&
                  itemToDelete.promo_id !== "") ||
                itemDetails?.isPromoted ||
                itemDetails?.isGetPromotion;

              const hasAnyAppliedPromotions = appliedPromotions.length > 0;

              const hasPromotedItemsInCart = cartItems.some(
                (item) =>
                  item.is_promoted === "1" ||
                  (item.promo_id !== null &&
                    item.promo_id !== undefined &&
                    item.promo_id !== "")
              );

              if (
                itemHasPromotions ||
                hasAnyAppliedPromotions ||
                hasPromotedItemsInCart
              ) {
                return (
                  <Typography
                    variant="body2"
                    color="warning.main"
                    sx={{ fontWeight: 500 }}
                  >
                    <Iconify
                      icon="mdi:warning"
                      width={24}
                      height={24}
                      sx={{ color: "warning.main", mr: 0.5 }}
                    />{" "}
                    Warning: Deleting this product may remove some promotion
                    items that are associated with it.
                  </Typography>
                );
              }

              return null;
            })()}
          </>
        }
        action={
          <>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDeleteItem}
              sx={{ ml: 1 }}
            >
              Delete
            </Button>
          </>
        }
      />

      <InventorySearchModal
        open={inventoryModalOpen}
        onClose={handleCloseInventoryModal}
        onProductSelect={handleInventoryProductSelectForQuantity}
        productOptions={productOptions}
        isLoading={isLoadingProductList}
        productSearchInput={productSearchInput}
        selectedCustomer={selectedCustomer}
        anchorPosition={inventoryModalAnchorPosition}
        onFocusOuterProductSearch={() => focusProductSearchInput(true)}
      />

      <NumericKeypad
        key={`rhp-qty-${selectedProductForQuantity?.id ?? "none"}-${quantityKeypadOpen ? "open" : "closed"}`}
        open={quantityKeypadOpen}
        onClose={handleQuantityKeypadClose}
        onConfirm={handleQuantityConfirmFromSearch}
        initialValue={selectedProductForQuantity?.qty || selectedProductForQuantity?.quantity || 1}
        title="Enter Quantity"
        error={keypadError}
        min={selectedProductForQuantity && isMeatsProduct(selectedProductForQuantity) ? 0.01 : 1}
        max={selectedProductForQuantity && isMeatsProduct(selectedProductForQuantity) ? 9999.99 : 9999}
        allowDecimal={selectedProductForQuantity ? isMeatsProduct(selectedProductForQuantity) : false}
        canSplit={selectedProductForQuantity ? String(selectedProductForQuantity.split || "0").trim() === "2" : false}
        initialOrderType={selectedOrderType}
      />
    </>
  );
};

export default NewEposRhp;
